import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { alertTradePlaced, alertTradeClosed } from "./alerts";
import TradeDrawer from "./TradeDrawer";
import TradeCards from "./TradeCards";
import Collapsed from "./Collapsed";
import { ERA_VIEWS } from "./eras";
import { isExpiryDay, tradingDaysOf } from "./expiry";
import { peakConcurrentCapital } from "./capitalUsage";

const API_MAP = { mstock: "/api/mstock", zerodha: "/api/zerodha" };

function exportTradesToCSV(trades, summary, filename = "backtest_results") {
  if (!trades || trades.length === 0) return;
  const summaryRows = [
    ["=== BACKTEST SUMMARY ==="],
    ["Total Trades", summary.totalTrades], ["Wins", summary.wins], ["Losses", summary.losses],
    ["Win Rate", `${summary.winRate}%`], ["Total Index P&L", summary.totalPnl],
    ["Total Option P&L (net)", `₹${summary.totalOptionPnl}`],
    ["Total Charges", `₹${summary.totalCharges || 0}`],
    ["Capital/Trade", `₹${summary.capitalPerTrade || 0}`],
    ["Total Capital Used", `₹${summary.totalCapitalUsed || 0}`],
    ["ROI", `${summary.roi || 0}%`], ["Profit Factor", summary.profitFactor],
    [""], ["=== TRADE LOG ==="],
  ];
  const headers = [
    "Type", "Fib Level", "Signal Date", "Exit Date", "Entry", "SL", "Target", "Exit Price",
    "Exit Reason", "Result", "Index P&L (pts)", "R Multiple",
    "Option Type", "Strike", "Option Symbol",
    "Buy Premium", "Sell Premium", "Capital Used",
    "Gross P&L (₹)", "Charges (₹)", "Net P&L (₹)", "Premium Source"
  ];
  const tradeRows = trades.map(t => [
    t.type, t.fibLevel || "", t.triggerDate, t.exitDate,
    t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.exitPrice?.toFixed(2),
    t.exitReason, t.result, t.pnl?.toFixed(2), t.rMultiple?.toFixed(2),
    t.option?.type || "", t.option?.strike || "", t.option?.optionSymbol || "",
    t.option?.entryPremium?.toFixed(2) || "", t.option?.exitPremium?.toFixed(2) || "",
    t.option?.entryPremium && t.option?.qty ? Math.round(t.option.entryPremium * t.option.qty) : "",
    t.option?.moneyPnlGross || "", t.option?.charges?.totalCharges || "", t.option?.moneyPnl || "",
    t.option?.premiumSource || "ESTIMATED",
  ]);
  const allRows = [...summaryRows, headers, ...tradeRows];
  const csvContent = allRows.map(row =>
    Array.isArray(row) ? row.map(cell => {
      const str = String(cell ?? "");
      return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",") : String(row)
  ).join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const LIVE_HEADERS = ["Time","Type","Option","Qty","Entry","SL","Target","Entry Prem","Exit Prem","Capital","Gross P&L","Net P&L","Status","Exit Reason","Strategy","SIM/LIVE"];
function liveTradeRows(trades) {
  return trades.map(t => [new Date(t.time||t.placedAt).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false}), t.type, (t.signalType||"")+" "+(t.strike||""), t.qty, t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.entryPremium?.toFixed(2)||"", t.exitPremium?.toFixed(2)||"", t.capital||"", t.grossPnl||"", t.netPnl||"", t.status, t.exitReason||"", t.strategy||"", t.simulated?"SIM":"LIVE"]);
}
function openLiveTradesInSheets(trades) {
  if (!trades?.length) return;
  const rows = liveTradeRows(trades);
  const tsv = [LIVE_HEADERS, ...rows].map(r => r.join("\t")).join("\n");
  const html = "<table><tr>" + LIVE_HEADERS.map(h => "<th>"+h+"</th>").join("") + "</tr>" + rows.map(r => "<tr>" + r.map(c => "<td>"+(c??"")+"</td>").join("") + "</tr>").join("") + "</table>";
  navigator.clipboard.write([new ClipboardItem({"text/html": new Blob([html], {type:"text/html"}), "text/plain": new Blob([tsv], {type:"text/plain"})})]).then(() => {
    window.open("https://docs.google.com/spreadsheets/create", "_blank");
    const t = document.createElement("div"); t.textContent = "Data copied! Press Ctrl+V in Google Sheets"; t.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a73e8;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;z-index:9999;"; document.body.appendChild(t); setTimeout(() => t.remove(), 5000);
  }).catch(() => { navigator.clipboard.writeText(tsv).then(() => { window.open("https://docs.google.com/spreadsheets/create", "_blank"); alert("Data copied! Paste with Ctrl+V"); }); });
}
function exportLiveTradesCSV(trades, filename) {
  if (!trades?.length) return;
  const headers = LIVE_HEADERS;
  const rows = trades.map(t => [new Date(t.time||t.placedAt).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false}), t.type, (t.signalType||"")+" "+(t.strike||""), t.qty, t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.entryPremium?.toFixed(2)||"", t.exitPremium?.toFixed(2)||"", t.capital||"", t.grossPnl||"", t.netPnl||"", t.status, t.exitReason||"", t.strategy||"", t.simulated?"SIM":"LIVE"]);
  const csv = [headers, ...rows].map(r => r.map(c => {const s=String(c??""); return s.includes(",") ? `"${s}"` : s;}).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
}

function openInGoogleSheets(trades, summary) {
  if (!trades || trades.length === 0) return;
  const headers = ["Type", "Fib", "Signal Date", "Exit Date", "Entry", "SL", "Target", "Exit",
    "Reason", "Result", "P&L", "R", "Option", "Strike", "Symbol",
    "Buy Prem", "Sell Prem", "Capital", "Gross P&L", "Charges", "Net P&L", "Source"];
  const rows = trades.map(t => [
    t.type, t.fibLevel || "", t.triggerDate, t.exitDate,
    t.entry?.toFixed(2), t.sl?.toFixed(2), t.target?.toFixed(2), t.exitPrice?.toFixed(2),
    t.exitReason, t.result, t.pnl?.toFixed(2), t.rMultiple?.toFixed(2),
    t.option?.type || "", t.option?.strike || "", t.option?.optionSymbol || "",
    t.option?.entryPremium?.toFixed(2) || "", t.option?.exitPremium?.toFixed(2) || "",
    t.option?.entryPremium && t.option?.qty ? Math.round(t.option.entryPremium * t.option.qty) : "",
    t.option?.moneyPnlGross || "", t.option?.charges?.totalCharges || "", t.option?.moneyPnl || "",
    t.option?.premiumSource || "EST",
  ]);
  const summaryBlock = [
    ["BACKTEST SUMMARY"],
    ["Trades", summary.totalTrades, "Wins", summary.wins, "Losses", summary.losses],
    ["Win Rate", `${summary.winRate}%`, "P&L", `${summary.totalOptionPnl}`, "Charges", `${summary.totalCharges || 0}`],
    ["Capital/Trade", summary.capitalPerTrade || 0, "ROI", `${summary.roi || 0}%`, "PF", summary.profitFactor],
    [],
  ];
  const tsv = [...summaryBlock, headers, ...rows].map(r => Array.isArray(r) ? r.join("\t") : r).join("\n");
  const htmlTable = `<table><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>${rows.map(r => `<tr>${r.map(c => `<td>${c ?? ""}</td>`).join("")}</tr>`).join("")}</table>`;
  navigator.clipboard.write([
    new ClipboardItem({
      "text/html": new Blob([htmlTable], { type: "text/html" }),
      "text/plain": new Blob([tsv], { type: "text/plain" }),
    })
  ]).then(() => {
    window.open("https://docs.google.com/spreadsheets/create", "_blank");
    const toast = document.createElement("div");
    toast.textContent = "Data copied! Press Ctrl+V (Cmd+V) in Google Sheets to paste";
    toast.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#1a73e8;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.3);";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
  }).catch(() => {
    navigator.clipboard.writeText(tsv).then(() => {
      window.open("https://docs.google.com/spreadsheets/create", "_blank");
      alert("Data copied to clipboard! Press Ctrl+V (Cmd+V) in Google Sheets to paste.");
    });
  });
}

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

const TF_MINUTES = { "5minute": 5, "15minute": 15, "30minute": 30, "60minute": 60, "day": 375 };
function formatDateRange(d, tf) {
  if (!d) return "";
  const start = new Date(d);
  if (isNaN(start)) return d;
  const mins = TF_MINUTES[tf] || 15;
  const end = new Date(start.getTime() + mins * 60000);
  const fmt = (dt) => dt.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  const datePart = start.toLocaleString("en-IN", { day: "2-digit", month: "short" });
  return `${datePart}, ${fmt(start)}-${fmt(end)}`;
}

/* ===== Fib Levels Display ===== */
function FibLevelsCard({ fibData, spotPrice }) {
  if (!fibData) return null;
  return (
    <div className="fib-levels-card">
      <h4>Fibonacci Levels ({fibData.isUptrend ? "Uptrend Retracement" : "Downtrend Retracement"})</h4>
      <div className="fib-swing-info">
        <span>Swing High: <strong>{fibData.swingHigh.price.toFixed(1)}</strong></span>
        <span>Swing Low: <strong>{fibData.swingLow.price.toFixed(1)}</strong></span>
      </div>
      <div className="fib-levels-list">
        {fibData.levels.map((lvl, i) => {
          const isNearSpot = spotPrice && Math.abs(spotPrice - lvl.price) < (fibData.swingHigh.price - fibData.swingLow.price) * 0.03;
          return (
            <div key={i} className={`fib-level-row ${isNearSpot ? "fib-level-active" : ""} ${lvl.level >= 0.382 && lvl.level <= 0.618 ? "fib-level-key" : ""}`}>
              <span className="fib-level-name">{lvl.name}</span>
              <div className="fib-level-bar">
                <div className="fib-level-fill" style={{ width: `${lvl.level * 100}%` }}></div>
              </div>
              <span className="fib-level-price">{lvl.price}</span>
              {isNearSpot && <span className="fib-near-badge">NEAR</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== S/R Zones Display ===== */
function SRZonesCard({ zones, spotPrice }) {
  if (!zones || zones.length === 0) return null;
  return (
    <div className="sr-zones-card">
      <h4>Support / Resistance Zones</h4>
      <div className="sr-zones-list">
        {zones.map((z, i) => {
          const isSupport = spotPrice && z.center < spotPrice;
          const isNear = spotPrice && Math.abs(spotPrice - z.center) < (spotPrice * 0.005);
          return (
            <div key={i} className={`sr-zone-row ${isNear ? "sr-zone-active" : ""}`}>
              <span className={`sr-zone-type ${isSupport ? "sr-support" : "sr-resistance"}`}>
                {isSupport ? "S" : "R"}
              </span>
              <span className="sr-zone-price">{z.center}</span>
              <span className="sr-zone-range">({z.low} - {z.high})</span>
              <span className="sr-zone-touches">{z.touches} touches</span>
              {isNear && <span className="fib-near-badge">NEAR</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== Stats Block (reused pattern) ===== */
function StatsBlock({ label, stats, color }) {
  if (!stats || stats.trades === 0) return null;
  const pf = stats.profitFactor === Infinity ? "INF" : stats.profitFactor;
  return (
    <div className={`bt-breakdown-section bt-dir-section bt-dir-${color}`}>
      <h4>{label} <span className="bt-dir-count">({stats.trades} trades)</span></h4>
      <div className="bt-dir-summary">
        <div className={`bt-dir-pnl ${stats.totalOptionPnl >= 0 ? "positive" : "negative"}`}>
          {stats.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(stats.totalOptionPnl || 0).toLocaleString("en-IN")}
        </div>
        <div className={`bt-dir-pnl ${stats.totalPnl >= 0 ? "positive" : "negative"}`} style={{fontSize:14}}>
          {stats.totalPnl >= 0 ? "+" : ""}{stats.totalPnl} pts
        </div>
        <div className={`bt-dir-wr ${stats.winRate >= 50 ? "positive" : "negative"}`}>{stats.winRate}% win</div>
        <div className={stats.profitFactor >= 1 ? "positive" : "negative"}>PF: {pf}</div>
      </div>
      <div className="bt-stat"><span>Wins / Losses</span><span>{stats.wins} / {stats.losses}</span></div>
      <div className="bt-stat"><span>Avg Win</span><span className="positive">+{stats.avgWin} pts</span></div>
      <div className="bt-stat"><span>Avg Loss</span><span className="negative">{stats.avgLoss} pts</span></div>
      <div className="bt-stat"><span>Avg R</span><span className={stats.avgR >= 0 ? "positive" : "negative"}>{stats.avgR >= 0 ? "+" : ""}{stats.avgR}R</span></div>
      <div className="bt-stat"><span>Max Drawdown</span><span>{stats.maxDrawdown} pts</span></div>
    </div>
  );
}

/* ===== Backtest Results ===== */
function BacktestResults({ bt, dirFilter }) {
  const s = bt.summary;
  const filteredTrades = dirFilter === "ALL" ? bt.trades : bt.trades.filter(t => t.type === dirFilter);
  const filteredEquity = [];
  let cum = 0, cumOpt = 0;
  for (const t of filteredTrades) {
    cum += t.pnl; cumOpt += t.option?.moneyPnl || 0;
    filteredEquity.push({ date: t.exitDate, pnl: cum, optPnl: cumOpt, type: t.type });
  }

  return (
    <div className="backtest-results">
      <h3>Backtest — {bt.symbol} ({bt.timeframe}, {bt.period})</h3>
      <p className="bt-candle-count">
        {bt.totalCandles} candles analyzed
        {bt.dataSource && <span className="bt-data-source"> | {bt.dataSource}</span>}
      </p>

      {bt.premiumStats && (
        <div className="premium-stats-bar">
          <span className="premium-stats-icon">$</span>
          <span>
            <strong>{bt.premiumStats.actualDataTrades}</strong> of {bt.premiumStats.totalTrades} trades with actual option premiums
            {bt.premiumStats.skippedTrades > 0 && (
              <span className="premium-skipped"> | {bt.premiumStats.skippedTrades} skipped ({bt.premiumStats.reason})</span>
            )}
          </span>
        </div>
      )}

      <div className={`bt-money-banner ${s.totalOptionPnl >= 0 ? "money-profit" : "money-loss"}`}>
        <div className="bt-money-amount">
          {s.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalOptionPnl).toLocaleString("en-IN")}
        </div>
        <div className="bt-money-detail">
          {s.lots} lots × {s.lotSize} qty = {s.qty} units | {s.strikeType} | {s.totalTrades} trades
        </div>
      </div>

      <div className="bt-summary-grid">
        <div className={`bt-card ${s.totalPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.totalPnl >= 0 ? "+" : ""}{s.totalPnl} pts</div>
          <div className="bt-card-label">Index P&L</div>
        </div>
        <div className="bt-card">
          <div className="bt-card-value">{s.totalTrades}</div>
          <div className="bt-card-label">Total Trades</div>
        </div>
        <div className={`bt-card ${s.winRate >= 50 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.winRate}%</div>
          <div className="bt-card-label">Win Rate</div>
        </div>
        <div className={`bt-card ${s.profitFactor >= 1 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-card-value">{s.profitFactor === Infinity ? "INF" : s.profitFactor}</div>
          <div className="bt-card-label">Profit Factor</div>
        </div>
        <div className="bt-card bt-card-red">
          <div className="bt-card-value">{s.maxDrawdown} pts</div>
          <div className="bt-card-label">Max Drawdown</div>
        </div>
        {s.capitalPerTrade > 0 && (
          <div className="bt-card">
            <div className="bt-card-value">₹{s.capitalPerTrade.toLocaleString("en-IN")}</div>
            <div className="bt-card-label">Capital/Trade</div>
          </div>
        )}
        {s.totalCapitalUsed > 0 && (
          <div className="bt-card">
            <div className="bt-card-value">₹{s.totalCapitalUsed.toLocaleString("en-IN")}</div>
            <div className="bt-card-label">Total Capital Used</div>
          </div>
        )}
        {s.roi !== undefined && s.roi !== 0 && (
          <div className={`bt-card ${s.roi >= 0 ? "bt-card-green" : "bt-card-red"}`}>
            <div className="bt-card-value">{s.roi >= 0 ? "+" : ""}{s.roi}%</div>
            <div className="bt-card-label">ROI</div>
          </div>
        )}
      </div>

      <div className="bt-dir-pnl-cards">
        <div className={`bt-dir-card bt-dir-card-short ${(bt.shortStats?.totalOptionPnl || 0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-dir-card-title">SHORT (Buy PUT)</div>
          <div className="bt-dir-card-optpnl">{(bt.shortStats?.totalOptionPnl || 0) >= 0 ? "+" : ""}₹{Math.abs(bt.shortStats?.totalOptionPnl || 0).toLocaleString("en-IN")}</div>
          <div className="bt-dir-card-meta">{bt.shortStats?.trades || 0} trades | {bt.shortStats?.winRate || 0}% win</div>
        </div>
        <div className={`bt-dir-card bt-dir-card-long ${(bt.longStats?.totalOptionPnl || 0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
          <div className="bt-dir-card-title">LONG (Buy CALL)</div>
          <div className="bt-dir-card-optpnl">{(bt.longStats?.totalOptionPnl || 0) >= 0 ? "+" : ""}₹{Math.abs(bt.longStats?.totalOptionPnl || 0).toLocaleString("en-IN")}</div>
          <div className="bt-dir-card-meta">{bt.longStats?.trades || 0} trades | {bt.longStats?.winRate || 0}% win</div>
        </div>
      </div>

      <div className="bt-breakdown">
        <StatsBlock label="SHORT (Buy PUT)" stats={bt.shortStats} color="short" />
        <StatsBlock label="LONG (Buy CALL)" stats={bt.longStats} color="long" />
      </div>

      {/* Equity Curve */}
      {filteredEquity.length > 0 && (
        <div className="bt-equity">
          <h4>Equity Curve ({filteredTrades.length} trades)</h4>
          <div className="bt-equity-chart">
            {(() => {
              const points = filteredEquity;
              const allVals = [...points.map(p => p.pnl), ...points.map(p => p.optPnl), 0];
              const maxVal = Math.max(...allVals); const minVal = Math.min(...allVals);
              const range = maxVal - minVal || 1; const h = 140;
              const w = points.length * 20 + 20;
              const yOf = v => h - ((v - minVal) / range) * h;
              return (
                <svg width="100%" height={h + 30} viewBox={`0 0 ${w} ${h + 30}`}>
                  <line x1="0" y1={yOf(0)} x2={w} y2={yOf(0)} stroke="var(--border-strong)" strokeDasharray="4" />
                  <polyline fill="none" stroke={points[points.length - 1]?.pnl >= 0 ? "#69f0ae" : "#ff5252"} strokeWidth="2"
                    points={points.map((p, i) => `${i * 20 + 10},${yOf(p.pnl)}`).join(" ")} />
                  <polyline fill="none" stroke="#ffc107" strokeWidth="2" strokeDasharray="6,3"
                    points={points.map((p, i) => `${i * 20 + 10},${yOf(p.optPnl)}`).join(" ")} />
                  {points.map((p, i) => (
                    <circle key={i} cx={i * 20 + 10} cy={yOf(p.pnl)} r="3" fill={p.type === "SHORT" ? "#ff5252" : "#69f0ae"}>
                      <title>{`${p.type}: ${p.pnl >= 0 ? "+" : ""}${p.pnl.toFixed(1)} pts`}</title>
                    </circle>
                  ))}
                </svg>
              );
            })()}
          </div>
        </div>
      )}

      {/* Trade Log */}
      <div className="bt-trade-log">
        <div className="trade-log-header">
          <h4>Trade Log ({filteredTrades.length} trades)</h4>
          <div className="export-buttons">
            <button className="btn btn-export" onClick={() => exportTradesToCSV(filteredTrades, s, `fibvwap_${bt.timeframe}`)}>
              Export CSV
            </button>
            <button className="btn btn-sheets" onClick={() => openInGoogleSheets(filteredTrades, s)} title="Copies data and opens Google Sheets — paste with Ctrl+V">
              Open in Sheets
            </button>
          </div>
        </div>
        <div className="stocks-table-wrapper">
          <table className="stocks-table">
            <thead>
              <tr><th>Type</th><th>Fib</th><th>Signal</th><th>Exit Time</th><th>Entry</th><th>SL</th><th>Target</th><th>Exit</th><th>Reason</th><th>P&L</th><th>Option</th><th>Buy Prem</th><th>Sell Prem</th><th>Capital</th><th>₹ P&L (net)</th><th>R</th></tr>
            </thead>
            <tbody>
              {filteredTrades.slice().reverse().map((t, idx) => (
                <React.Fragment key={idx}>
                <tr className={t.result === "WIN" ? "trade-win" : t.result === "LOSS" ? "trade-loss" : ""}>
                  <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                  <td><span className="fib-badge">{t.fibLevel}</span></td>
                  <td title={t.exactEntryTime ? new Date(t.exactEntryTime).toLocaleString("en-IN") : t.triggerDate}>
                    {t.exactEntryTime ? formatDate(t.exactEntryTime) : formatDateRange(t.triggerDate, bt.timeframe)}
                  </td>
                  <td title={t.exactExitTime ? new Date(t.exactExitTime).toLocaleString("en-IN") : t.exitDate}>
                    {t.exactExitTime ? formatDate(t.exactExitTime) : formatDateRange(t.exitDate, bt.timeframe)}
                  </td>
                  <td>{t.entry.toFixed(1)}</td>
                  <td className="sl">{t.sl.toFixed(1)}</td>
                  <td>{t.target.toFixed(1)}</td>
                  <td>{t.exitPrice.toFixed(1)}</td>
                  <td><span className={`exit-reason reason-${t.result.toLowerCase()}`}>{t.exitReason}</span></td>
                  <td className={t.pnl >= 0 ? "positive" : "negative"}>{t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(1)}</td>
                  <td className="opt-cell">
                    {t.option ? `${t.option.type} ${t.option.strike}` : "-"}
                    {t.option?.premiumSource === "ACTUAL"
                      ? <span className="actual-badge">ACTUAL</span>
                      : <span className="est-badge">EST</span>}
                    {t.option?.optionSymbol && <div className="opt-symbol-small">{t.option.optionSymbol}</div>}
                  </td>
                  <td className="premium-cell">
                    {t.option?.entryPremium ? `₹${t.option.entryPremium.toFixed(1)}` : "-"}
                  </td>
                  <td className="premium-cell">
                    {t.option?.exitPremium ? `₹${t.option.exitPremium.toFixed(1)}` : "-"}
                  </td>
                  <td className="premium-cell">
                    {t.option?.entryPremium ? `₹${Math.round(t.option.entryPremium * t.option.qty).toLocaleString("en-IN")}` : "-"}
                  </td>
                  <td className={t.option?.moneyPnl >= 0 ? "positive" : "negative"}
                    title={t.option ? `Gross: ₹${t.option.moneyPnlGross} | Charges: ₹${t.option.charges?.totalCharges}` : ""}>
                    {t.option ? `${t.option.moneyPnl >= 0 ? "+" : ""}₹${Math.abs(t.option.moneyPnl).toLocaleString("en-IN")}` : "-"}
                  </td>
                  <td className={t.rMultiple >= 0 ? "positive" : "negative"}>{t.rMultiple >= 0 ? "+" : ""}{t.rMultiple.toFixed(2)}R</td>
                </tr>
                {t.trailExitDone && (
                  <tr className="trail-detail-row">
                    <td colSpan={2} className="trail-detail-label">Exit 1: Trail SL</td>
                    <td>{Math.floor((t.option?.qty || 130) / 2)} qty ({Math.floor((t.option?.lots || 2) / 2) || 1}L)</td>
                    <td>{t.trailExitDate ? formatDate(t.trailExitDate) : "-"}</td>
                    <td colSpan={2}>{t.trailExitPrice?.toFixed(1)}</td>
                    <td className={t.trailExitPnl >= 0 ? "positive" : "negative"}>{t.trailExitPnl >= 0 ? "+" : ""}{t.trailExitPnl} pts</td>
                    <td colSpan={2} className="trail-detail-label">Exit 2: {t.exitReason}</td>
                    <td>{(t.option?.qty || 130) - Math.floor((t.option?.qty || 130) / 2)} qty</td>
                    <td>{t.exitPrice?.toFixed(1)}</td>
                    <td colSpan={4} className={t.pnl >= 0 ? "positive" : "negative"}>Combined: {t.pnl >= 0 ? "+" : ""}{t.pnl.toFixed(1)} pts</td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ===== Help Modal ===== */
function HelpModal({ onClose }) {
  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div className="help-dialog" onClick={e => e.stopPropagation()}>
        <div className="help-header">
          <h3>Fib + VWAP + S/R — Quick Guide</h3>
          <button className="chart-close" onClick={onClose}>X</button>
        </div>
        <div className="help-content">
          <div className="help-section">
            <h4>How Signals Are Generated</h4>
            <p><strong>1. Swing Detection:</strong> Finds swing highs/lows using a lookback window (default 5 candles each side).</p>
            <p><strong>2. Fibonacci Levels:</strong> Draws retracement from the latest swing high/low pair. Only levels between 23.6% and 78.6% are used for signals.</p>
            <p><strong>3. Confluence Check:</strong> Price must touch a Fib level (within 0.5×ATR) AND that level must align (within 0.8×ATR) with VWAP or an S/R zone. Without confluence, the signal is skipped.</p>
          </div>
          <div className="help-section">
            <h4>LONG Signal (Buy CALL)</h4>
            <p><strong>Signal candle:</strong> Bullish candle (or closes in upper 60% of range) that dips to a Fib support level and closes above it.</p>
            <p><strong>Trigger:</strong> Next candle breaks above the signal candle's high.</p>
            <p><strong>Entry:</strong> Signal candle's high. <strong>SL:</strong> Signal candle's low - 0.2×ATR.</p>
            <p><strong>Target:</strong> Entry + (risk × target multiple). Default 2R.</p>
          </div>
          <div className="help-section">
            <h4>SHORT Signal (Buy PUT)</h4>
            <p><strong>Signal candle:</strong> Bearish candle (or closes in lower 60% of range) that reaches a Fib resistance level and closes below it.</p>
            <p><strong>Trigger:</strong> Next candle breaks below the signal candle's low.</p>
            <p><strong>Entry:</strong> Signal candle's low. <strong>SL:</strong> Signal candle's high + 0.2×ATR.</p>
            <p><strong>Target:</strong> Entry - (risk × target multiple). Default 2R.</p>
          </div>
          <div className="help-section">
            <h4>Risk Filter</h4>
            <p>Signals with risk ≤ 0 or risk &gt; 2×ATR are filtered out to avoid bad risk/reward setups.</p>
          </div>
          <div className="help-section">
            <h4>S/R Zones</h4>
            <p>Auto-detected from price action — areas where price has touched 3+ times within an ATR-based tolerance. More touches = stronger zone.</p>
          </div>
          <div className="help-section">
            <h4>VWAP</h4>
            <p>Volume-Weighted Average Price acts as dynamic intraday S/R. Confluence is checked within 0.8×ATR of the Fib level.</p>
          </div>
          <div className="help-section">
            <h4>Trade Execution</h4>
            <p><strong>Option:</strong> ATM CE for LONG, ATM PE for SHORT. BUY order at market price.</p>
            <p><strong>Monitor:</strong> Backend checks Nifty LTP every 3 seconds against SL/target.</p>
            <p><strong>Trailing SL:</strong> Once favorable by 1×risk, SL moves to breakeven. Then trails by 0.75×ATR as price moves further.</p>
            <p><strong>Partial exit:</strong> Books ~75% of lots when premium is +20% of capital, trails the remaining 25% to target.</p>
          </div>
          <div className="help-section">
            <h4>Auto-Trading</h4>
            <p><strong>Scan interval:</strong> Configurable (10s to 5m). Scans for new Fib confluence signals.</p>
            <p><strong>Simulate mode:</strong> Paper trades with SIM order IDs — no real orders placed.</p>
            <p><strong>Live mode:</strong> Places real BUY orders on Zerodha. Monitor handles exit automatically.</p>
            <p><strong>Direction filter:</strong> Trade both, long-only, or short-only.</p>
          </div>
          <div className="help-section help-warning">
            <h4>Important</h4>
            <p>Live trading places real orders with real money. Always test with SIMULATE first. Use at your own risk.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Trade Chart Modal ===== */
function TradeChartModal({ trade, chartData, signals, fibData, srZones, vwap, onClose }) {
  const N = chartData?.length || 0;

  // Previous trading day high/low relative to the trade's date.
  // Falls back to second-latest day in the dataset if trade date isn't found.
  const pdhPdl = useMemo(() => {
    if (!N) return null;
    const byDate = {};
    chartData.forEach(c => {
      const d = new Date(c.date).toISOString().split("T")[0];
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(c);
    });
    const dates = Object.keys(byDate).sort();
    if (dates.length < 2) return null;
    const tradeDateStr = trade?.time ? new Date(trade.time).toISOString().split("T")[0] : null;
    const tradeIdx = tradeDateStr ? dates.indexOf(tradeDateStr) : -1;
    const prevDateKey = tradeIdx > 0 ? dates[tradeIdx - 1] : dates[dates.length - 2];
    const bars = byDate[prevDateKey];
    return {
      pdh: Math.max(...bars.map(c => c.high)),
      pdl: Math.min(...bars.map(c => c.low)),
      date: prevDateKey,
    };
  }, [chartData, trade?.time, N]);

  // Auto-bounds derived from the full dataset — stable anchors for the sliders
  const autoBounds = useMemo(() => {
    if (!N) return { min: 0, max: 0 };
    let lo = Math.min(...chartData.map(c => c.low));
    let hi = Math.max(...chartData.map(c => c.high));
    if (fibData?.levels) {
      fibData.levels.forEach(l => { lo = Math.min(lo, l.price); hi = Math.max(hi, l.price); });
    }
    if (pdhPdl) {
      lo = Math.min(lo, pdhPdl.pdl);
      hi = Math.max(hi, pdhPdl.pdh);
    }
    const r = hi - lo || 1;
    return { min: lo - r * 0.02, max: hi + r * 0.02 };
  }, [chartData, fibData, pdhPdl, N]);

  const [xStart, setXStart] = useState(0);
  const [xEnd, setXEnd] = useState(Math.max(0, N - 1));
  const [yMin, setYMin] = useState(autoBounds.min);
  const [yMax, setYMax] = useState(autoBounds.max);
  const [crosshair, setCrosshair] = useState(null);
  const [dragAnchor, setDragAnchor] = useState(null);
  const [dateSliderIdx, setDateSliderIdx] = useState(0); // 0 = all days, 1..N = specific day
  const [fullscreen, setFullscreen] = useState(false);

  // Unique sorted dates in chartData, plus a lookup of first/last bar index per date
  const dateIndex = useMemo(() => {
    if (!N) return { dates: [], bounds: {} };
    const bounds = {};
    chartData.forEach((c, i) => {
      const d = new Date(c.date).toISOString().split("T")[0];
      if (!bounds[d]) bounds[d] = { first: i, last: i };
      else bounds[d].last = i;
    });
    const dates = Object.keys(bounds).sort();
    return { dates, bounds };
  }, [chartData, N]);

  // Reset sliders whenever a new trade / new data is loaded
  useEffect(() => {
    setXStart(0);
    setXEnd(Math.max(0, N - 1));
    setYMin(autoBounds.min);
    setYMax(autoBounds.max);
    setDateSliderIdx(0);
  }, [chartData, autoBounds.min, autoBounds.max, N]);

  function applyDateSlider(raw) {
    const v = parseInt(raw);
    setDateSliderIdx(v);
    if (v === 0) {
      setXStart(0);
      setXEnd(Math.max(0, N - 1));
    } else {
      const d = dateIndex.dates[v - 1];
      const b = dateIndex.bounds[d];
      if (b) { setXStart(b.first); setXEnd(b.last); }
    }
  }

  function formatShortDate(iso) {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  }

  if (!chartData || N === 0) return null;

  // Dark halo so colored labels drawn over candles stay readable even when the
  // candle behind them is the same color (e.g. green signal text over a green bar).
  const textOutline = { paintOrder: "stroke", stroke: "var(--panel2)", strokeWidth: 3, strokeLinejoin: "round" };

  const W = 900, H = 420, PAD = { top: 30, right: 80, bottom: 40, left: 60 };
  const cw = W - PAD.left - PAD.right;
  const ch = H - PAD.top - PAD.bottom;

  const visibleCount = Math.max(1, xEnd - xStart + 1);
  const minP = Math.min(yMin, yMax - 1);
  const maxP = Math.max(yMax, yMin + 1);
  const priceRange = maxP - minP;

  // Slider bounds for price: expand past auto range so user can zoom out
  const ySliderPad = (autoBounds.max - autoBounds.min) * 0.5 || 50;
  const ySliderLo = autoBounds.min - ySliderPad;
  const ySliderHi = autoBounds.max + ySliderPad;

  const x = (i) => PAD.left + ((i - xStart) / Math.max(1, visibleCount - 1)) * cw;
  const y = (p) => PAD.top + (1 - (p - minP) / priceRange) * ch;
  const candleW = Math.max(2, Math.min(8, cw / visibleCount * 0.7));
  const inX = (i) => i >= xStart && i <= xEnd;

  // Get SVG-space (viewBox) coordinates from a mouse event.
  // Uses getScreenCTM so it stays correct under preserveAspectRatio letterboxing
  // (e.g. fullscreen when container aspect != viewBox aspect).
  function eventToSvgCoords(e) {
    const svg = e.currentTarget;
    const ctm = svg.getScreenCTM && svg.getScreenCTM();
    if (ctm && svg.createSVGPoint) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const p = pt.matrixTransform(ctm.inverse());
      return { mx: p.x, my: p.y };
    }
    // Fallback (shouldn't happen in modern browsers)
    const rect = svg.getBoundingClientRect();
    return {
      mx: (e.clientX - rect.left) * (W / rect.width),
      my: (e.clientY - rect.top) * (H / rect.height),
    };
  }

  function handleMouseDown(e) {
    const { mx, my } = eventToSvgCoords(e);
    if (mx < PAD.left || mx > W - PAD.right || my < PAD.top || my > H - PAD.bottom) return;
    setDragAnchor({ mx, my, xStart0: xStart, xEnd0: xEnd, yMin0: yMin, yMax0: yMax });
  }

  function handleMouseMove(e) {
    const { mx, my } = eventToSvgCoords(e);

    // Drag-pan: translate visible window so point under cursor stays anchored
    if (dragAnchor) {
      const dx = mx - dragAnchor.mx;
      const dy = my - dragAnchor.my;
      const width = dragAnchor.xEnd0 - dragAnchor.xStart0;
      const barShift = Math.round(-dx / cw * Math.max(1, width));
      let newXStart = dragAnchor.xStart0 + barShift;
      let newXEnd = dragAnchor.xEnd0 + barShift;
      if (newXStart < 0) { newXStart = 0; newXEnd = width; }
      if (newXEnd > N - 1) { newXEnd = N - 1; newXStart = Math.max(0, newXEnd - width); }
      setXStart(newXStart);
      setXEnd(newXEnd);
      const priceShift = (dy / ch) * (dragAnchor.yMax0 - dragAnchor.yMin0);
      setYMin(dragAnchor.yMin0 + priceShift);
      setYMax(dragAnchor.yMax0 + priceShift);
    }

    // Crosshair
    if (mx < PAD.left || mx > W - PAD.right || my < PAD.top || my > H - PAD.bottom) {
      setCrosshair(null);
      return;
    }
    const rel = (mx - PAD.left) / cw;
    const barIdx = Math.round(xStart + rel * Math.max(1, visibleCount - 1));
    setCrosshair({ x: mx, y: my, barIdx });
  }

  function handleMouseUp() { setDragAnchor(null); }
  const crosshairPrice = crosshair ? minP + (1 - (crosshair.y - PAD.top) / ch) * priceRange : null;
  const crosshairBar = crosshair && chartData[crosshair.barIdx];

  // Price grid
  const gridLines = 6;
  const gridStep = priceRange / gridLines;

  // Find signal indices in chart data
  const signalMarkers = (signals || []).map(sig => {
    const sigDate = sig.triggerCandle?.date || sig.signalCandle?.date;
    const idx = chartData.findIndex(c => c.date === sigDate);
    return { ...sig, chartIdx: idx };
  }).filter(s => s.chartIdx >= 0);

  // Trade entry/SL/target lines
  const entryY = trade.entry ? y(trade.entry) : null;
  const slY = trade.sl ? y(trade.sl) : null;
  const targetY = trade.target ? y(trade.target) : null;

  return (
    <div className="confirm-overlay" onClick={onClose}>
      <div className="help-dialog"
        style={fullscreen
          ? { maxWidth: "none", maxHeight: "none", width: "100vw", height: "100vh", margin: 0, borderRadius: 0, display: "flex", flexDirection: "column" }
          : { maxWidth: 960, width: "95vw" }}
        onClick={e => e.stopPropagation()}>
        <div className="help-header">
          <h3>
            {trade.type} {trade.signalType} {trade.strike} — {trade.fibLevel || ""} Fib
            <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 8, color: "var(--dim2)" }}>
              {new Date(trade.time).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
            </span>
          </h3>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => setFullscreen(f => !f)}
              title={fullscreen ? "Exit full screen" : "Full screen"}
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>
              {fullscreen ? "Exit Full" : "Full Screen"}
            </button>
            <button className="chart-close" onClick={onClose}>X</button>
          </div>
        </div>
        <div style={fullscreen
          ? { flex: 1, minHeight: 0, padding: "8px 16px", display: "flex", alignItems: "stretch", justifyContent: "center" }
          : { overflowX: "auto", padding: "8px 0" }}>
          <svg
            width={fullscreen ? "100%" : W}
            height={fullscreen ? "100%" : H}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ background: "var(--panel2)", borderRadius: 8, cursor: dragAnchor ? "grabbing" : "crosshair", userSelect: "none" }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => { setCrosshair(null); setDragAnchor(null); }}>
            {/* Price grid */}
            {Array.from({ length: gridLines + 1 }, (_, i) => {
              const price = minP + i * gridStep;
              const py = y(price);
              return (
                <g key={`grid-${i}`}>
                  <line x1={PAD.left} y1={py} x2={W - PAD.right} y2={py} stroke="var(--panel3)" strokeWidth={1} />
                  <text x={W - PAD.right + 4} y={py + 4} fill="var(--muted2)" fontSize={10}>{price.toFixed(0)}</text>
                </g>
              );
            })}

            {/* P&L zone shading (behind fib/candles): green = entry→target, red = entry→SL */}
            {trade.entry && trade.target && (() => {
              const yTop = Math.max(PAD.top, Math.min(y(trade.entry), y(trade.target)));
              const yBot = Math.min(H - PAD.bottom, Math.max(y(trade.entry), y(trade.target)));
              if (yBot <= yTop) return null;
              return <rect x={PAD.left} y={yTop} width={cw} height={yBot - yTop} fill="#22c55e" opacity={0.09} />;
            })()}
            {trade.entry && trade.sl && (() => {
              const yTop = Math.max(PAD.top, Math.min(y(trade.entry), y(trade.sl)));
              const yBot = Math.min(H - PAD.bottom, Math.max(y(trade.entry), y(trade.sl)));
              if (yBot <= yTop) return null;
              return <rect x={PAD.left} y={yTop} width={cw} height={yBot - yTop} fill="#ef4444" opacity={0.09} />;
            })()}

            {/* Fib levels */}
            {fibData?.levels?.map((lvl, i) => {
              const py = y(lvl.price);
              if (py < PAD.top || py > H - PAD.bottom) return null;
              const isKey = lvl.level >= 0.382 && lvl.level <= 0.618;
              return (
                <g key={`fib-${i}`}>
                  <line x1={PAD.left} y1={py} x2={W - PAD.right} y2={py} stroke={isKey ? "#7c4dff" : "#7c4dff55"} strokeWidth={isKey ? 1.2 : 0.8} strokeDasharray={isKey ? "none" : "4,4"} />
                  <text x={PAD.left - 4} y={py + 3} fill="#7c4dff" fontSize={9} textAnchor="end" style={textOutline}>{lvl.name}</text>
                </g>
              );
            })}

            {/* S/R zones */}
            {(srZones || []).map((z, i) => {
              const zy = y(z.center);
              if (zy < PAD.top || zy > H - PAD.bottom) return null;
              return (
                <g key={`sr-${i}`}>
                  <rect x={PAD.left} y={y(z.high)} width={cw} height={Math.abs(y(z.low) - y(z.high))} fill="#ffa72610" />
                  <line x1={PAD.left} y1={zy} x2={W - PAD.right} y2={zy} stroke="#ffa726" strokeWidth={0.8} strokeDasharray="3,3" />
                  <text x={W - PAD.right + 4} y={zy + 3} fill="#ffa726" fontSize={8}>S/R {z.touches}t</text>
                </g>
              );
            })}

            {/* Previous-day high/low (amber, dashed) */}
            {pdhPdl && (() => {
              const yH = y(pdhPdl.pdh);
              const yL = y(pdhPdl.pdl);
              const showH = yH >= PAD.top && yH <= H - PAD.bottom;
              const showL = yL >= PAD.top && yL <= H - PAD.bottom;
              return (
                <g>
                  {showH && (
                    <>
                      <line x1={PAD.left} y1={yH} x2={W - PAD.right} y2={yH}
                        stroke="#fbbf24" strokeWidth={1} strokeDasharray="8,4" opacity={0.85} />
                      <text x={W - PAD.right - 4} y={yH - 3} fill="#fbbf24" fontSize={9} textAnchor="end" fontWeight="bold" style={textOutline}>
                        PDH {pdhPdl.pdh.toFixed(1)}
                      </text>
                    </>
                  )}
                  {showL && (
                    <>
                      <line x1={PAD.left} y1={yL} x2={W - PAD.right} y2={yL}
                        stroke="#fbbf24" strokeWidth={1} strokeDasharray="8,4" opacity={0.85} />
                      <text x={W - PAD.right - 4} y={yL + 10} fill="#fbbf24" fontSize={9} textAnchor="end" fontWeight="bold" style={textOutline}>
                        PDL {pdhPdl.pdl.toFixed(1)}
                      </text>
                    </>
                  )}
                </g>
              );
            })()}

            {/* VWAP line */}
            {chartData.some(c => c.vwap) && (
              <polyline
                points={chartData.map((c, i) => (inX(i) && c.vwap) ? `${x(i)},${y(c.vwap)}` : "").filter(Boolean).join(" ")}
                fill="none" stroke="#26c6da" strokeWidth={1.2} opacity={0.7}
              />
            )}

            {/* EMA20 line */}
            {chartData.some(c => c.ema20) && (
              <polyline
                points={chartData.map((c, i) => (inX(i) && c.ema20) ? `${x(i)},${y(c.ema20)}` : "").filter(Boolean).join(" ")}
                fill="none" stroke="#ffa726" strokeWidth={1} opacity={0.5}
              />
            )}

            {/* Candlesticks */}
            {chartData.map((c, i) => {
              if (!inX(i)) return null;
              const cx = x(i);
              const isGreen = c.close >= c.open;
              const color = isGreen ? "#26a69a" : "#ef5350";
              return (
                <g key={`c-${i}`}>
                  <line x1={cx} y1={y(c.high)} x2={cx} y2={y(c.low)} stroke={color} strokeWidth={1} />
                  <rect
                    x={cx - candleW / 2} y={y(Math.max(c.open, c.close))}
                    width={candleW} height={Math.max(1, Math.abs(y(c.open) - y(c.close)))}
                    fill={isGreen ? color : color} stroke={color} strokeWidth={0.5}
                  />
                </g>
              );
            })}

            {/* Trade entry/SL/target lines */}
            {entryY != null && (
              <g>
                <line x1={PAD.left} y1={entryY} x2={W - PAD.right} y2={entryY} stroke="#42a5f5" strokeWidth={1.5} strokeDasharray="6,3" />
                <text x={PAD.left + 4} y={entryY - 4} fill="#42a5f5" fontSize={10} fontWeight="bold" style={textOutline}>Entry {trade.entry?.toFixed(1)}</text>
              </g>
            )}
            {slY != null && (
              <g>
                <line x1={PAD.left} y1={slY} x2={W - PAD.right} y2={slY} stroke="#ff5252" strokeWidth={1.2} strokeDasharray="4,4" />
                <text x={PAD.left + 4} y={slY - 4} fill="#ff5252" fontSize={10} fontWeight="bold" style={textOutline}>SL {trade.sl?.toFixed(1)}</text>
              </g>
            )}
            {targetY != null && (
              <g>
                <line x1={PAD.left} y1={targetY} x2={W - PAD.right} y2={targetY} stroke="#66bb6a" strokeWidth={1.2} strokeDasharray="4,4" />
                <text x={PAD.left + 4} y={targetY - 4} fill="#66bb6a" fontSize={10} fontWeight="bold" style={textOutline}>Target {trade.target?.toFixed(1)}</text>
              </g>
            )}

            {/* Signal markers */}
            {signalMarkers.map((sig, i) => {
              if (!inX(sig.chartIdx)) return null;
              const sx = x(sig.chartIdx);
              const sy = sig.type === "LONG" ? y(sig.entryPrice) + 14 : y(sig.entryPrice) - 8;
              const color = sig.type === "LONG" ? "#66bb6a" : "#ef5350";
              const arrow = sig.type === "LONG" ? "▲" : "▼";
              return (
                <g key={`sig-${i}`}>
                  <text x={sx} y={sy} fill={color} fontSize={14} textAnchor="middle" fontWeight="bold" style={textOutline}>{arrow}</text>
                  <text x={sx} y={sy + (sig.type === "LONG" ? 12 : -6)} fill={color} fontSize={8} textAnchor="middle" fontWeight="bold" style={textOutline}>{sig.fibLevel}</text>
                </g>
              );
            })}

            {/* Legend */}
            <g transform={`translate(${PAD.left + 8}, ${PAD.top + 8})`}>
              <rect x={0} y={0} width={210} height={66} rx={4} fill="color-mix(in srgb, var(--panel2) 80%, transparent)" />
              <line x1={8} y1={12} x2={24} y2={12} stroke="#26c6da" strokeWidth={1.5} />
              <text x={28} y={15} fill="var(--dim2)" fontSize={9}>VWAP</text>
              <line x1={58} y1={12} x2={74} y2={12} stroke="#ffa726" strokeWidth={1} />
              <text x={78} y={15} fill="var(--dim2)" fontSize={9}>EMA20</text>
              <line x1={118} y1={12} x2={134} y2={12} stroke="#fbbf24" strokeWidth={1} strokeDasharray="4,2" />
              <text x={138} y={15} fill="var(--dim2)" fontSize={9}>PDH/PDL</text>
              <line x1={8} y1={26} x2={24} y2={26} stroke="#7c4dff" strokeWidth={1.2} />
              <text x={28} y={29} fill="var(--dim2)" fontSize={9}>Fib Levels</text>
              <line x1={74} y1={26} x2={90} y2={26} stroke="#ffa726" strokeWidth={0.8} strokeDasharray="3,3" />
              <text x={94} y={29} fill="var(--dim2)" fontSize={9}>S/R Zones</text>
              <rect x={8} y={36} width={12} height={8} fill="#22c55e" opacity={0.3} />
              <text x={24} y={44} fill="var(--dim2)" fontSize={9}>Profit</text>
              <rect x={54} y={36} width={12} height={8} fill="#ef4444" opacity={0.3} />
              <text x={70} y={44} fill="var(--dim2)" fontSize={9}>Loss</text>
              <text x={8} y={58} fill="#66bb6a" fontSize={10}>▲</text><text x={18} y={58} fill="var(--dim2)" fontSize={9}>Long</text>
              <text x={50} y={58} fill="#ef5350" fontSize={10}>▼</text><text x={60} y={58} fill="var(--dim2)" fontSize={9}>Short</text>
              <text x={98} y={58} fill="var(--dim2)" fontSize={9}>{signalMarkers.length} signal{signalMarkers.length !== 1 ? "s" : ""}</text>
            </g>

            {/* Crosshair overlay */}
            {crosshair && (
              <g pointerEvents="none">
                <line x1={crosshair.x} y1={PAD.top} x2={crosshair.x} y2={H - PAD.bottom}
                  stroke="var(--dim2)" strokeWidth={0.8} strokeDasharray="3,3" opacity={0.7} />
                <line x1={PAD.left} y1={crosshair.y} x2={W - PAD.right} y2={crosshair.y}
                  stroke="var(--dim2)" strokeWidth={0.8} strokeDasharray="3,3" opacity={0.7} />
                {/* Price label at right edge */}
                <rect x={W - PAD.right + 1} y={crosshair.y - 8} width={54} height={16}
                  fill="var(--panel3)" stroke="var(--muted2)" strokeWidth={0.5} rx={2} />
                <text x={W - PAD.right + 5} y={crosshair.y + 4} fill="var(--text2)" fontSize={10} fontWeight="bold">
                  {crosshairPrice != null ? crosshairPrice.toFixed(1) : ""}
                </text>
                {/* Time label at bottom */}
                {crosshairBar && (
                  <>
                    <rect x={crosshair.x - 44} y={H - PAD.bottom + 2} width={88} height={14}
                      fill="var(--panel3)" stroke="var(--muted2)" strokeWidth={0.5} rx={2} />
                    <text x={crosshair.x} y={H - PAD.bottom + 12} fill="var(--text2)" fontSize={9} textAnchor="middle">
                      {new Date(crosshairBar.date).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}
                    </text>
                  </>
                )}
                {/* OHLC readout near top of chart */}
                {crosshairBar && (
                  <g transform={`translate(${PAD.left + 220}, ${PAD.top + 6})`}>
                    <rect x={0} y={0} width={220} height={18} rx={3} fill="color-mix(in srgb, var(--panel2) 80%, transparent)" stroke="var(--border2)" strokeWidth={0.5} />
                    <text x={6} y={12} fill="var(--dim2)" fontSize={9}>
                      O <tspan fill="var(--text2)" fontWeight="bold">{crosshairBar.open.toFixed(1)}</tspan>
                      {"  "}H <tspan fill="#26a69a" fontWeight="bold">{crosshairBar.high.toFixed(1)}</tspan>
                      {"  "}L <tspan fill="#ef5350" fontWeight="bold">{crosshairBar.low.toFixed(1)}</tspan>
                      {"  "}C <tspan fill="var(--text2)" fontWeight="bold">{crosshairBar.close.toFixed(1)}</tspan>
                    </text>
                  </g>
                )}
              </g>
            )}

            {/* X-axis time labels — only visible slice, ~8 labels evenly spaced */}
            {chartData.map((c, i) => {
              if (!inX(i)) return null;
              const step = Math.max(1, Math.floor(visibleCount / 8));
              if ((i - xStart) % step !== 0) return null;
              return (
                <text key={`xl-${i}`} x={x(i)} y={H - 8} fill="var(--muted2)" fontSize={9} textAnchor="middle">
                  {new Date(c.date).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false })}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Axis sliders */}
        <div style={{ padding: "4px 16px 10px", borderTop: "1px solid var(--panel3)" }}>
          {dateIndex.dates.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 10, color: "var(--dim2)", marginTop: 6 }}>
              <span style={{ minWidth: 38 }}>Date</span>
              <input type="range" min={0} max={dateIndex.dates.length} value={dateSliderIdx}
                onChange={e => applyDateSlider(e.target.value)} style={{ flex: 1 }}
                title="0 = all days; drag to snap to a single day" />
              <span style={{ minWidth: 110, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: "bold", color: "var(--text2)" }}>
                {dateSliderIdx === 0 ? `All (${dateIndex.dates.length} days)` : formatShortDate(dateIndex.dates[dateSliderIdx - 1])}
              </span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 10, color: "var(--dim2)", marginTop: 6 }}>
            <span style={{ minWidth: 38 }}>Time</span>
            <input type="range" min={0} max={Math.max(0, N - 1)} value={xStart}
              onChange={e => setXStart(Math.min(parseInt(e.target.value), xEnd - 1))} style={{ flex: 1 }} title="Start bar" />
            <input type="range" min={0} max={Math.max(0, N - 1)} value={xEnd}
              onChange={e => setXEnd(Math.max(parseInt(e.target.value), xStart + 1))} style={{ flex: 1 }} title="End bar" />
            <span style={{ minWidth: 70, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{visibleCount}/{N} bars</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 10, color: "var(--dim2)", marginTop: 6 }}>
            <span style={{ minWidth: 38 }}>Price</span>
            <input type="range" min={ySliderLo} max={ySliderHi} step={0.5} value={yMin}
              onChange={e => setYMin(Math.min(parseFloat(e.target.value), yMax - 5))} style={{ flex: 1 }} title="Y min" />
            <input type="range" min={ySliderLo} max={ySliderHi} step={0.5} value={yMax}
              onChange={e => setYMax(Math.max(parseFloat(e.target.value), yMin + 5))} style={{ flex: 1 }} title="Y max" />
            <span style={{ minWidth: 110, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{minP.toFixed(0)} – {maxP.toFixed(0)}</span>
            <button
              onClick={() => { setXStart(0); setXEnd(N - 1); setYMin(autoBounds.min); setYMax(autoBounds.max); setDateSliderIdx(0); }}
              style={{ fontSize: 10, padding: "2px 10px", background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, cursor: "pointer" }}>
              Reset
            </button>
          </div>
        </div>
        <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--dim2)", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span>Entry: <strong style={{ color: "#42a5f5" }}>{trade.entry?.toFixed(1)}</strong></span>
          <span>SL: <strong style={{ color: "#ff5252" }}>{trade.sl?.toFixed(1)}</strong></span>
          <span>Target: <strong style={{ color: "#66bb6a" }}>{trade.target?.toFixed(1)}</strong></span>
          <span>Risk: <strong>{trade.risk?.toFixed(1)}</strong> pts</span>
          <span>Fib: <strong style={{ color: "#7c4dff" }}>{trade.fibLevel}</strong></span>
          {trade.status === "CLOSED" && <span>Exit: <strong>{trade.exitReason}</strong></span>}
        </div>
      </div>
    </div>
  );
}

/* ===== MAIN COMPONENT ===== */
function pickDeepItmStrike(optionChain, isPut) {
  const step = optionChain.strikeStep || 50;
  const spot = optionChain.spotPrice ?? optionChain.atmStrike;
  const base = Math.floor(spot / step) * step;
  return isPut ? base + 2 * step : base - step;
}

const lotsForIndex = (ix) => ix === "SENSEX" ? "10" : "5";

export default function FibVwap({ broker = "mstock", index = "NIFTY" }) {
  const API = API_MAP[broker] || API_MAP.mstock;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("15minute");
  const [days, setDays] = useState("1");
  const [scanTarget, setScanTarget] = useState(() => localStorage.getItem("fv_scanTarget") || "2");
  const [scanLots, setScanLots] = useState(() => localStorage.getItem("fv_scanLots") || lotsForIndex(index));
  const [scanStrike, setScanStrike] = useState(() => localStorage.getItem("fv_scanStrike") || "ATM");
  useEffect(() => { localStorage.setItem("fv_scanLots", scanLots); }, [scanLots]);
  useEffect(() => { localStorage.setItem("fv_scanTarget", scanTarget); }, [scanTarget]);
  useEffect(() => { localStorage.setItem("fv_scanStrike", scanStrike); }, [scanStrike]);
  // Reset lots to the index-appropriate default when the index changes (skip initial mount to preserve persisted/initial values)
  const lotsIdxInit = useRef(true);
  useEffect(() => {
    if (lotsIdxInit.current) { lotsIdxInit.current = false; return; }
    setScanLots(lotsForIndex(index));
    setBtLots(lotsForIndex(index));
  }, [index]);
  const [useVwap, setUseVwap] = useState(true);
  const [useSR, setUseSR] = useState(true);
  const [tradeDirection, setTradeDirection] = useState("BOTH");
  const [vixAdaptive, setVixAdaptive] = useState(true);

  // Trade guards
  const [maxTradesPerDay, setMaxTradesPerDay] = useState("10");
  const [skipFirstMins, setSkipFirstMins] = useState("15");

  // Auto-scan
  const [scanning, setScanning] = useState(false);
  const [scanInterval, setScanInterval] = useState("10"); // seconds
  const [lastScanTime, setLastScanTime] = useState(null);
  const scanIntervalRef = useRef(null);

  // Auto-trade
  const [autoTrading, setAutoTrading] = useState(false);
  const [simulateMode, setSimulateMode] = useState(true);
  const [tradeLog, setTradeLog] = useState([]);
  const [drawerTrade, setDrawerTrade] = useState(null);

  // Load persisted trades from backend on mount — only fib-vwap trades
  useEffect(() => {
    axios.get(`/api/monitor/status?strategy=fib-vwap&index=${index}`).then(res => {
      if (res.data.success && res.data.trades?.length > 0) {
        setTradeLog(res.data.trades.map(t => ({
          ...t,
          id: t.id || Date.now() + Math.random(),
          time: t.placedAt || t.time,
          signalType: t.type === "SHORT" ? "PE" : "CE",
        })));
      }
    }).catch(() => {});
  }, []);
  const [tradeError, setTradeError] = useState("");
  const placingTradeRef = useRef(false); // lock to prevent duplicate placements
  const processedSignalsRef = useRef(new Set()); // track processed signal keys
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Trade chart modal
  const [chartTrade, setChartTrade] = useState(null);
  const [chartModalData, setChartModalData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const openTradeChart = useCallback(async (trade) => {
    setChartTrade(trade);
    // Always fetch 5-minute data for chart readability, independent of scan timeframe
    setChartLoading(true);
    try {
      const tradeDate = new Date(trade.time || trade.placedAt);
      const now = new Date();
      const diffDays = Math.max(1, Math.ceil((now - tradeDate) / (1000 * 60 * 60 * 24)));
      const res = await axios.get(`${API}/fib-vwap`, {
        // +1 day ensures previous-day bars are present for PDH/PDL computation
        params: { timeframe: "5minute", days: String(diffDays + 1), vwap: "true", sr: "true", index },
        timeout: 30000,
      });
      if (res.data.success) {
        setChartModalData(res.data.data);
      }
    } catch {}
    setChartLoading(false);
  }, [API, index]);

  // Trade log filters
  const [tlFilterStatus, setTlFilterStatus] = useState("ALL");
  const [tlFilterType, setTlFilterType] = useState("ALL");
  const [tlFilterMode, setTlFilterMode] = useState("ALL");
  const [tlFilterExpiry, setTlFilterExpiry] = useState("ALL"); // ALL | EXPIRY | NONEXP
  const [tlFilterDate, setTlFilterDate] = useState(() => new Date().toLocaleDateString("en-CA")); // range FROM, default: today
  const [tlFilterDateTo, setTlFilterDateTo] = useState(""); // range TO ("" = open-ended)
  // Keep the today-default fresh: the PWA/browser can stay mounted across days,
  // which froze the From-date at mount time. If the user hasn't changed it,
  // roll it to the new day on focus/resume/day-change.
  const tlAutoDate = useRef(new Date().toLocaleDateString("en-CA"));
  useEffect(() => {
    const refresh = () => {
      const today = new Date().toLocaleDateString("en-CA");
      if (today !== tlAutoDate.current) {
        setTlFilterDate(prev => (prev === tlAutoDate.current ? today : prev));
        tlAutoDate.current = today;
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const iv = setInterval(refresh, 60000);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); clearInterval(iv); };
  }, []);

  const [tlFilterResult, setTlFilterResult] = useState("ALL");
  const [liveNiftyPrice, setLiveNiftyPrice] = useState(null);
  const monitorRef = useRef(null);

  // Backtest
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState("");
  const [btTimeframe, setBtTimeframe] = useState("15minute");
  const [btDays, setBtDays] = useState("1");
  const [btDateMode, setBtDateMode] = useState("custom");
  const [btFromDate, setBtFromDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [btToDate, setBtToDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [btTarget, setBtTarget] = useState("2");
  const [btTrailing, setBtTrailing] = useState(true);
  const [btLots, setBtLots] = useState(lotsForIndex(index));
  const [btStrike, setBtStrike] = useState("ATM");
  const [btPartial, setBtPartial] = useState(true);
  const [btVwap, setBtVwap] = useState(true);
  const [btSR, setBtSR] = useState(true);
  const [btVixAdaptive, setBtVixAdaptive] = useState(true);
  const [btDirFilter, setBtDirFilter] = useState("ALL");

  // --- Scan ---
  const runScan = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/fib-vwap`, {
        params: { timeframe, days, vwap: useVwap ? "true" : "false", sr: useSR ? "true" : "false", index },
        timeout: 30000,
      });
      if (res.data.success) { setData(res.data.data); setLastScanTime(new Date()); return res.data.data; }
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setLoading(false); }
    return null;
  }, [timeframe, days, useVwap, useSR, index, API]);

  // --- Auto-trade: place order ---
  const placeAutoTrade = useCallback(async (signal, optionTrade) => {
    if (!signal || !optionTrade?.suggestions?.length) return;
    const suggestion = optionTrade.suggestions[0];
    const defaultLotSize = index === "BANKNIFTY" ? 30 : index === "SENSEX" ? 20 : 65;
    const lotSize = parseInt(suggestion.lotSize) || defaultLotSize;
    const qty = parseInt(scanLots) * lotSize;
    try {
      let orderId = null;
      if (!simulateMode) {
        const orderExchange = index === "SENSEX" ? "BFO" : "NFO";
        const orderRes = await axios.post(`${API}/place-order`, {
          tradingsymbol: suggestion.symbol, exchange: orderExchange, transaction_type: "BUY",
          quantity: qty, order_type: "MARKET", product: "MIS",
        });
        if (!orderRes.data.success) return null;
        orderId = orderRes.data.data?.orderId;
      } else {
        orderId = "SIM-" + Date.now();
      }

      // Fetch actual index LTP for real entry price (not signal candle's level)
      let actualEntry = signal.entryPrice;
      try {
        const idxRes = await axios.get(`${API}/index-ltp`, { params: { index }, timeout: 5000 });
        if (idxRes.data.success && idxRes.data.ltp) {
          actualEntry = idxRes.data.ltp;
        }
      } catch {}

      // Recalculate SL and target from actual entry, preserving risk ratio
      const risk = signal.risk;
      const actualSL = signal.type === "SHORT" ? actualEntry + risk : actualEntry - risk;
      const targetMultiple = parseFloat(scanTarget);
      const actualTarget = signal.type === "SHORT"
        ? actualEntry - risk * targetMultiple
        : actualEntry + risk * targetMultiple;

      // Fetch actual entry premium
      let entryPremium = null;
      try {
        const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: suggestion.token, tradingsymbol: suggestion.symbol }, timeout: 5000 });
        if (ltpRes.data.success) entryPremium = ltpRes.data.ltp;
      } catch {}

      // Conservative target premium estimate: delta 0.4 to stay achievable after
      // theta decay + exit slippage (theoretical ATM delta is ~0.5)
      const targetPremium = entryPremium
        ? Math.round((entryPremium + 0.4 * Math.abs(actualTarget - actualEntry)) * 10) / 10
        : null;

      const tradeEntry = {
        id: Date.now(), time: new Date().toISOString(), type: signal.type,
        signalType: signal.type === "SHORT" ? "PE" : "CE",
        tradingsymbol: suggestion.symbol, optionToken: suggestion.token,
        strike: suggestion.strike, strikeType: suggestion.type,
        qty, lots: parseInt(scanLots),
        entry: Math.round(actualEntry * 10) / 10,
        sl: Math.round(actualSL * 10) / 10,
        target: Math.round(actualTarget * 10) / 10,
        signalEntry: signal.entryPrice, // keep original for reference
        risk: signal.risk, fibLevel: signal.fibLevel,
        orderId, status: "OPEN", simulated: simulateMode, strategy: "fib-vwap", index,
        entryPremium, targetPremium,
        capital: entryPremium ? Math.round(entryPremium * qty) : null,
      };
      setTradeLog(prev => [tradeEntry, ...prev]);
      alertTradePlaced(tradeEntry);
      await axios.post(`${API}/auto-trade/track`, tradeEntry);
      return tradeEntry;
    } catch (err) {
      setTradeError(`${simulateMode ? "[SIM] " : ""}Order failed: ${err.response?.data?.message || err.message}`);
    }
    return null;
  }, [scanLots, scanTarget, simulateMode, API]);

  // --- Exit trade ---
  const exitTrade = useCallback(async (trade) => {
    try {
      // For LIVE trades, hit the broker's exit endpoint
      if (!trade.simulated) {
        const res = await axios.post(`${API}/exit-order`, {
          tradingsymbol: trade.tradingsymbol, exchange: "NFO", quantity: trade.qty, transaction_type: "BUY",
        });
        if (!res.data.success) {
          setTradeError(`Exit failed: ${res.data.message}`);
          return;
        }
      }
      // Fetch current premium for P&L (works for both SIM and LIVE)
      let exitPremium = null;
      try {
        const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: trade.optionToken, tradingsymbol: trade.tradingsymbol }, timeout: 5000 });
        if (ltpRes.data.success) exitPremium = ltpRes.data.ltp;
      } catch {}

      const ep = trade.entryPremium || 0;
      const xp = exitPremium || ep;
      const qty = trade.qty || 130;
      const grossPnl = Math.round((xp - ep) * qty);
      const bv = ep * qty, sv = xp * qty, tv = bv + sv;
      const brk = Math.min(20, tv * 0.0003) * 2;
      const charges = Math.round((brk + sv * 0.000625 + tv * 0.00053 + (brk + tv * 0.00053) * 0.18 + tv * 0.000001 + bv * 0.00003) * 100) / 100;
      const netPnl = Math.round(grossPnl - charges);

      const closedTrade = {
        ...trade, status: "CLOSED", exitTime: new Date().toISOString(),
        exitReason: "MANUAL EXIT", exitPremium, grossPnl, netPnl, charges,
      };
      setTradeLog(prev => prev.map(t => t.id === trade.id ? closedTrade : t));
      alertTradeClosed(closedTrade);
      await axios.post(`${API}/auto-trade/update`, {
        id: trade.id, status: "CLOSED", exitTime: closedTrade.exitTime,
        exitReason: "MANUAL EXIT", exitPremium, grossPnl, netPnl, charges,
      }).catch(() => {});
    } catch (err) { setTradeError(`Exit failed: ${err.response?.data?.message || err.message}`); }
  }, [API]);

  // --- Start/Stop scan ---
  const toggleScan = useCallback(() => {
    if (scanning) {
      if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
      setScanning(false);
    } else {
      setScanning(true); runScan();
      scanIntervalRef.current = setInterval(() => { runScan(); }, parseInt(scanInterval) * 1000);
    }
  }, [scanning, runScan]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      if (monitorRef.current) clearInterval(monitorRef.current);
    };
  }, []);

  // --- Backend monitor status polling + live P&L ---
  const [monitorStatus, setMonitorStatus] = useState(null);
  const [livePremiums, setLivePremiums] = useState({});
  const tradeLogRef = useRef(tradeLog);
  tradeLogRef.current = tradeLog; // always current // { tradingsymbol: currentPremium }

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`/api/monitor/status?strategy=fib-vwap&index=${index}`, { timeout: 5000 });
        if (res.data.success) {
          setMonitorStatus(res.data);
          const brokerKey = broker || "mstock";
          if (res.data.lastLTP?.[brokerKey]) setLiveNiftyPrice(res.data.lastLTP[brokerKey]);

          // Sync all trades from backend (handles new + status changes)
          const bt = res.data.trades || [];
          if (bt.length > 0) {
            setTradeLog(prev => {
              const map = new Map(prev.map(t => [String(t.id), t]));
              for (const t of bt) {
                const key = String(t.id);
                map.set(key, { ...map.get(key), ...t, time: t.placedAt || t.time, signalType: t.type === "SHORT" ? "PE" : "CE" });
              }
              return Array.from(map.values()).sort((a, b) => new Date(b.time || b.placedAt || 0) - new Date(a.time || a.placedAt || 0));
            });
          }
        }
      } catch {}

      // Fetch live option premiums for open trades + backfill missing entryPremium
      for (const t of tradeLogRef.current.filter(t => t.status === "OPEN" && t.tradingsymbol)) {
        try {
          const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: t.optionToken, tradingsymbol: t.tradingsymbol }, timeout: 3000 });
          if (ltpRes.data.success && ltpRes.data.ltp) {
            const ltp = ltpRes.data.ltp;
            setLivePremiums(prev => ({ ...prev, [t.tradingsymbol]: ltp }));
            // Backfill entryPremium if missing (e.g. auth was down at trade time)
            if (!t.entryPremium) {
              setTradeLog(prev => prev.map(tr =>
                tr.id === t.id && !tr.entryPremium
                  ? { ...tr, entryPremium: ltp, capital: Math.round(ltp * (tr.qty || 130)) }
                  : tr
              ));
              // Persist backfilled premium to backend
              axios.post(`${API}/auto-trade/update`, { id: t.id, entryPremium: ltp, capital: Math.round(ltp * (t.qty || 130)) }).catch(() => {});
            }
          }
        } catch {}
      }
    };
    poll();
    monitorRef.current = setInterval(poll, 5000);
    return () => { if (monitorRef.current) { clearInterval(monitorRef.current); monitorRef.current = null; } };
  }, [broker, API, index]); // minimal deps — no tradeLog to avoid interval thrashing

  // --- Auto-trade signal detection (with lock and guards) ---
  useEffect(() => {
    if (!autoTrading || !data?.signals?.length || placingTradeRef.current) return;
    const latestSignal = data.signals[data.signals.length - 1];
    if (tradeDirection !== "BOTH" && latestSignal.type !== tradeDirection) return;

    // Unique key for this signal — prevents processing same signal twice
    const signalKey = `${latestSignal.type}_${latestSignal.entryPrice}_${latestSignal.triggerCandle?.date}`;
    if (processedSignalsRef.current.has(signalKey)) return;

    const alreadyTraded = tradeLog.some(t => t.entry === latestSignal.entryPrice && t.type === latestSignal.type);
    if (alreadyTraded) return;

    // --- GUARD 1: Time-of-day filter — skip signals in first N minutes ---
    const skipMins = parseInt(skipFirstMins) || 15;
    const sigDate = latestSignal.signalCandle?.date || latestSignal.triggerCandle?.date;
    if (sigDate) {
      const sigTime = new Date(sigDate);
      const marketOpen = new Date(sigTime); marketOpen.setHours(9, 15, 0, 0);
      if (sigTime.toDateString() === marketOpen.toDateString() && (sigTime - marketOpen) < skipMins * 60 * 1000) {
        processedSignalsRef.current.add(signalKey);
        setTradeError(`Skipped: signal in first ${skipMins} mins (${sigTime.toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit",hour12:false})})`);
        return;
      }
    }

    // --- GUARD 2: Max trades per day ---
    const maxPerDay = parseInt(maxTradesPerDay) || 5;
    const today = new Date().toISOString().split("T")[0];
    const todayTrades = tradeLog.filter(t => (t.time || t.placedAt || "").startsWith(today) && t.strategy === "fib-vwap");
    if (todayTrades.length >= maxPerDay) {
      processedSignalsRef.current.add(signalKey);
      setTradeError(`Skipped: max ${maxPerDay} trades/day reached (${todayTrades.length} today)`);
      return;
    }

    // --- GUARD 3: Staleness check — skip if Nifty moved > 0.5×risk from signal entry ---
    const lastClose = data.lastCandle?.close;
    if (lastClose) {
      const drift = Math.abs(lastClose - latestSignal.entryPrice);
      if (drift > latestSignal.risk * 0.5) {
        processedSignalsRef.current.add(signalKey);
        setTradeError(`Skipped: stale signal (Nifty ${lastClose.toFixed(0)} drifted ${drift.toFixed(0)} pts from entry ${latestSignal.entryPrice.toFixed(0)}, > 0.5×risk ${(latestSignal.risk*0.5).toFixed(0)})`);
        return;
      }
    }

    // --- GUARD 4: Trend filter — check EMA20 slope from chart data ---
    if (data.chartData?.length >= 5) {
      const recent = data.chartData.slice(-5);
      const ema20Values = recent.map(c => c.ema20).filter(v => v != null);
      if (ema20Values.length >= 3) {
        const emaSlope = ema20Values[ema20Values.length - 1] - ema20Values[0];
        const isTrendUp = emaSlope > 0;
        const isTrendDown = emaSlope < 0;
        if (latestSignal.type === "LONG" && isTrendDown && Math.abs(emaSlope) > latestSignal.risk * 0.3) {
          processedSignalsRef.current.add(signalKey);
          setTradeError(`Skipped: LONG against downtrend (EMA20 slope ${emaSlope.toFixed(1)})`);
          return;
        }
        if (latestSignal.type === "SHORT" && isTrendUp && Math.abs(emaSlope) > latestSignal.risk * 0.3) {
          processedSignalsRef.current.add(signalKey);
          setTradeError(`Skipped: SHORT against uptrend (EMA20 slope +${emaSlope.toFixed(1)})`);
          return;
        }
      }
    }

    // Build option trade from option chain if not already attached
    let optionTrade = latestSignal.optionTrade;
    if (!optionTrade && data.optionChain?.strikes?.length > 0) {
      const atmStrike = data.optionChain.atmStrike;
      const isPut = latestSignal.type === "SHORT";
      const want = pickDeepItmStrike(data.optionChain, isPut);
      const strikeData = data.optionChain.strikes.find(s => s.strike === want) || data.optionChain.strikes.find(s => s.strike === atmStrike);
      const opt = isPut ? strikeData?.pe : strikeData?.ce;
      if (opt) {
        optionTrade = {
          direction: isPut ? "BEARISH" : "BULLISH",
          suggestions: [{
            action: isPut ? "BUY PE" : "BUY CE",
            strike: strikeData.strike,
            symbol: opt.symbol,
            token: opt.token,
            lotSize: opt.lotSize || 65,
            type: "ITM+1",
          }],
        };
      }
    }

    if (optionTrade) {
      placingTradeRef.current = true;
      processedSignalsRef.current.add(signalKey);
      placeAutoTrade(latestSignal, optionTrade).finally(() => { placingTradeRef.current = false; });
    }
  }, [data, autoTrading, tradeLog, placeAutoTrade, tradeDirection, maxTradesPerDay, skipFirstMins]);

  // Sync lots/target/strike to server scanner config when changed mid-session
  useEffect(() => {
    if (!autoTrading) return;
    axios.post(`${API}/auto-trade/start`, {
      lots: parseInt(scanLots), strikeType: scanStrike, targetMultiple: parseFloat(scanTarget),
    }).catch(() => {});
  }, [scanLots, scanTarget, scanStrike, autoTrading, API]);

  // --- Toggle auto-trade ---
  const toggleAutoTrade = useCallback(async () => {
    if (autoTrading) {
      const openTrades = tradeLog.filter(t => t.status === "OPEN");
      if (openTrades.length > 0) { setShowStopConfirm(true); return; }
      setAutoTrading(false);
      await axios.post(`${API}/auto-trade/stop`).catch(() => {});
    } else {
      setAutoTrading(true);
      await axios.post(`${API}/auto-trade/start`, { lots: parseInt(scanLots), strikeType: scanStrike, targetMultiple: parseFloat(scanTarget) }).catch(() => {});
      if (!scanning) toggleScan();
    }
  }, [autoTrading, tradeLog, scanLots, scanStrike, scanTarget, scanning, toggleScan]);

  const handleStopSquareOff = useCallback(async () => {
    for (const trade of tradeLog.filter(t => t.status === "OPEN")) await exitTrade(trade);
    setAutoTrading(false); setShowStopConfirm(false);
    await axios.post(`${API}/auto-trade/stop`).catch(() => {});
  }, [tradeLog, exitTrade]);

  const handleStopKeepOpen = useCallback(async () => {
    setAutoTrading(false); setShowStopConfirm(false);
    await axios.post(`${API}/auto-trade/stop`).catch(() => {});
  }, []);

  // --- Backtest ---
  async function runBacktest() {
    setBtLoading(true); setBtError("");
    try {
      const btParams = {
        timeframe: btTimeframe,
        days: btDateMode === "custom" ? "0" : btDays,
        ...(btDateMode === "custom" && { fromDate: btFromDate, toDate: btToDate }),
        target: btTarget, trailing: btTrailing ? "true" : "false",
        lots: btLots, strike: btStrike,
        vwap: btVwap ? "true" : "false", sr: btSR ? "true" : "false", partial: btPartial ? "true" : "false",
        vix_adaptive: btVixAdaptive ? "true" : "false",
        index,
      };
      const res = await axios.get(`${API}/fib-vwap/backtest`, {
        params: btParams,
        timeout: 120000,
      });
      if (res.data.success) setBtData(res.data.data);
      else setBtError(res.data.message);
    } catch (err) { setBtError(err.response?.data?.message || err.message); }
    setBtLoading(false);
  }

  return (
    <div className="five-ema">
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {chartTrade && chartModalData && !chartLoading && (
        <TradeChartModal
          trade={chartTrade}
          chartData={chartModalData.chartData}
          signals={chartModalData.signals}
          fibData={chartModalData.fibData}
          srZones={chartModalData.srZones}
          vwap={chartModalData.vwapCurrent}
          onClose={() => { setChartTrade(null); setChartModalData(null); }}
        />
      )}
      {chartLoading && (
        <div className="confirm-overlay" onClick={() => { setChartTrade(null); setChartLoading(false); }}>
          <div className="help-dialog" style={{ maxWidth: 300, textAlign: "center", padding: 40 }} onClick={e => e.stopPropagation()}>
            Loading chart...
          </div>
        </div>
      )}

      {/* Stop Confirmation Dialog */}
      {showStopConfirm && (
        <div className="confirm-overlay" onClick={() => setShowStopConfirm(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">!</div>
            <h3>Active Trades Open</h3>
            <p>You have <strong>{tradeLog.filter(t => t.status === "OPEN").length}</strong> open trade(s).</p>
            <div className="confirm-actions">
              <button className="btn btn-danger" onClick={handleStopSquareOff}>Square Off All & Stop</button>
              <button className="btn btn-warning" onClick={handleStopKeepOpen}>Keep Open & Stop</button>
              <button className="btn btn-secondary" onClick={() => setShowStopConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>Fib + VWAP + S/R <button className="help-btn" onClick={() => setShowHelp(true)} title="Help">?</button></h2>
          <p className="dashboard-subtitle">Fibonacci Retracement with VWAP & Support/Resistance Confluence</p>
        </div>
      </div>

      {/* Strategy is hardcoded now — scan/config controls collapsed by default */}
      <Collapsed title="Scan & strategy controls (hardcoded — expand if needed)">
        <div className="dashboard-actions">
          <select className="ema-select" value={timeframe} onChange={e => setTimeframe(e.target.value)} disabled={scanning}>
            <option value="5minute">5 Min</option><option value="15minute">15 Min</option>
            <option value="30minute">30 Min</option><option value="60minute">1 Hour</option><option value="day">Daily</option>
          </select>
          <select className="ema-select" value={days} onChange={e => setDays(e.target.value)} disabled={scanning}>
            <option value="1">1 Day</option><option value="2">2 Days</option><option value="3">3 Days</option><option value="5">5 Days</option>
            <option value="10">10 Days</option><option value="30">30 Days</option>
            <option value="60">60 Days</option><option value="90">90 Days</option>
          </select>
          <select className="ema-select" value={scanTarget} onChange={e => setScanTarget(e.target.value)} disabled={scanning}>
            <option value="1">Target 1:1</option><option value="1.5">Target 1:1.5</option>
            <option value="2">Target 1:2</option><option value="3">Target 1:3</option>
          </select>
          <select className="ema-select" value={scanLots} onChange={e => setScanLots(e.target.value)} disabled={scanning}>
            <option value="1">1 Lot</option><option value="2">2 Lots</option>
            <option value="3">3 Lots</option><option value="5">5 Lots</option>
            <option value="10">10 Lots</option><option value="15">15 Lots</option>
            <option value="20">20 Lots</option><option value="25">25 Lots</option><option value="50">50 Lots</option>
          </select>
          <select className="ema-select" value={scanInterval} onChange={e => setScanInterval(e.target.value)} disabled={scanning}>
            <option value="10">10s</option>
            <option value="30">30s</option>
            <option value="60">1m</option>
            <option value="300">5m</option>
          </select>
          <button className={`btn ${scanning ? "btn-danger" : "btn-primary"}`} onClick={toggleScan} disabled={loading}>
            {loading ? "Scanning..." : scanning ? "Stop Scan" : "Start Scan"}
          </button>
          <select className="ema-select direction-select" value={tradeDirection} onChange={e => setTradeDirection(e.target.value)}>
            <option value="BOTH">Both</option>
            <option value="LONG">Long Only</option>
            <option value="SHORT">Short Only</option>
          </select>
          <button className={`btn ${autoTrading ? "btn-danger" : "btn-success"}`} onClick={toggleAutoTrade}>
            {autoTrading ? "Stop Trading" : "Start Trading"}
          </button>
          <label className={`sim-toggle ${simulateMode ? "sim-on" : "sim-off"}`} title={simulateMode ? "Paper trading — no real orders" : "LIVE — real orders!"}>
            <input type="checkbox" checked={simulateMode} onChange={e => setSimulateMode(e.target.checked)} disabled={autoTrading} />
            {simulateMode ? "SIMULATE" : "LIVE"}
          </label>
        </div>

      {/* Filters */}
      <div className="ema-filters">
        <span className="filter-label">Confluence:</span>
        <label className="filter-toggle">
          <input type="checkbox" checked={useVwap} onChange={e => setUseVwap(e.target.checked)} />
          <span className="filter-name">VWAP</span>
        </label>
        <label className="filter-toggle">
          <input type="checkbox" checked={useSR} onChange={e => setUseSR(e.target.checked)} />
          <span className="filter-name">S/R Zones</span>
        </label>
        <label className="filter-toggle" title="VIX-adaptive: scale targets, require dual confluence, limit trades in low VIX">
          <input type="checkbox" checked={vixAdaptive} onChange={e => setVixAdaptive(e.target.checked)} />
          <span className="filter-name" style={{color: "#f59e0b"}}>VIX Adaptive</span>
        </label>
        <span className="filter-label" style={{marginLeft: 12}}>Guards:</span>
        <select className="ema-select" value={maxTradesPerDay} onChange={e => setMaxTradesPerDay(e.target.value)} title="Max trades per day" style={{minWidth: 70}}>
          <option value="3">3/day</option><option value="5">5/day</option><option value="10">10/day</option><option value="99">No limit</option>
        </select>
        <select className="ema-select" value={skipFirstMins} onChange={e => setSkipFirstMins(e.target.value)} title="Skip signals in first N minutes" style={{minWidth: 85}}>
          <option value="0">No skip</option><option value="15">Skip 15m</option><option value="30">Skip 30m</option><option value="45">Skip 45m</option>
        </select>
      </div>
      </Collapsed>

      {/* Status Bar */}
      {(scanning || autoTrading) && (
        <div className={`auto-status-bar ${autoTrading ? "auto-status-trading" : "auto-status-scanning"}`}>
          <div className="auto-status-left">
            {scanning && (
              <span className="auto-status-item"><span className="pulse-dot"></span>Auto-Scan ON ({parseInt(scanInterval) < 60 ? `${scanInterval}s` : `${Math.round(parseInt(scanInterval)/60)}m`})
                {lastScanTime && <span className="last-scan-time"> | Last: {lastScanTime.toLocaleTimeString("en-IN")}</span>}
              </span>
            )}
            {autoTrading && (
              <span className="auto-status-item auto-status-trade-on"><span className="pulse-dot pulse-green"></span>
                {simulateMode ? "PAPER TRADING" : "LIVE TRADING"} | {scanLots} lots | Target {scanTarget}R | {tradeDirection === "BOTH" ? "Long+Short" : tradeDirection + " Only"}
                {" | "}{maxTradesPerDay !== "99" ? `${maxTradesPerDay}/day` : "No limit"}
                {parseInt(skipFirstMins) > 0 ? ` | Skip ${skipFirstMins}m` : ""}
                {" | Staleness+Trend"}
              </span>
            )}
          </div>
          <div className="auto-status-right">
            {liveNiftyPrice && tradeLog.some(t => t.status === "OPEN") && (
              <span className="live-nifty-price">Nifty: <strong>{liveNiftyPrice.toFixed(1)}</strong><span className="monitor-speed"> {monitorStatus?.monitoring ? "(backend 3s)" : "(waiting)"}</span></span>
            )}
            {tradeLog.filter(t => t.status === "OPEN").length > 0 && (
              <span className="open-trades-count">{tradeLog.filter(t => t.status === "OPEN").length} open</span>
            )}
          </div>
        </div>
      )}

      {tradeError && <div className="error-msg">{tradeError}</div>}

      {/* Trade Log */}
      {tradeLog.length > 0 && (() => {
        const filteredTradeLog = tradeLog.filter(t => {
          if (tlFilterStatus !== "ALL" && t.status !== tlFilterStatus) return false;
          if (tlFilterType !== "ALL" && t.type !== tlFilterType) return false;
          if (tlFilterMode === "SIM" && !t.simulated) return false;
          if (tlFilterMode === "LIVE" && t.simulated) return false;
          if (tlFilterExpiry === "EXPIRY" && !isExpiryDay(t)) return false;
          if (tlFilterExpiry === "NONEXP" && isExpiryDay(t)) return false;
          if (tlFilterDate || tlFilterDateTo) {
            const tradeDate = new Date(t.time || t.placedAt).toLocaleDateString("en-CA");
            if (tlFilterDate && tradeDate < tlFilterDate) return false;
            if (tlFilterDateTo && tradeDate > tlFilterDateTo) return false;
          }
          if (tlFilterResult !== "ALL") {
            if (tlFilterResult === "WIN" && !(t.status === "CLOSED" && t.netPnl > 0)) return false;
            if (tlFilterResult === "LOSS" && !(t.status === "CLOSED" && t.netPnl <= 0)) return false;
          }
          return true;
        });
        const tlSummary = filteredTradeLog.reduce((acc, t) => {
          if (t.status === "CLOSED" && t.netPnl != null) { acc.totalPnl += t.netPnl; acc.closed++; if (t.netPnl > 0) acc.wins++; }
          if (t.status === "OPEN") { acc.open++; if (t.trailExitPnl) acc.totalPnl += t.trailExitPnl; }
          return acc;
        }, { totalPnl: 0, wins: 0, closed: 0, open: 0 });
        return (
        <div className="trade-log-section">
          <div className="trade-log-header">
            <h3>Trade Log {autoTrading && <span className="live-badge">LIVE</span>}</h3>
            <div className="export-buttons">
              <button className="btn btn-export" onClick={() => exportLiveTradesCSV(filteredTradeLog, "fib-vwap-trades")}>Export CSV</button>
              <button className="btn btn-sheets" onClick={() => openLiveTradesInSheets(filteredTradeLog)}>Open in Sheets</button>
              {tlSummary.closed > 0 && (
                <button className="btn btn-sm btn-danger" onClick={async () => {
                  if (!window.confirm(`Delete all ${tlSummary.closed} closed trades? This cannot be undone.`)) return;
                  await axios.post(`${API}/auto-trade/delete-trades`, { deleteAllClosed: true, index }).catch(() => {});
                  setTradeLog(prev => prev.filter(t => t.status === "OPEN"));
                }}>Clear Closed ({tlSummary.closed})</button>
              )}
            </div>
          </div>
          <div className="ema-filters" style={{marginBottom: 8, gap: 6, flexWrap: "wrap"}}>
            <select className="ema-select" value={tlFilterStatus} onChange={e => setTlFilterStatus(e.target.value)} style={{minWidth: 80}}>
              <option value="ALL">All Status</option><option value="OPEN">Open</option><option value="CLOSED">Closed</option>
            </select>
            <select className="ema-select" value={tlFilterType} onChange={e => setTlFilterType(e.target.value)} style={{minWidth: 80}}>
              <option value="ALL">All Types</option><option value="SHORT">Short (PE)</option><option value="LONG">Long (CE)</option>
            </select>
            <select className="ema-select" value={tlFilterMode} onChange={e => setTlFilterMode(e.target.value)} style={{minWidth: 80}}>
              <option value="ALL">SIM + Live</option><option value="SIM">SIM Only</option><option value="LIVE">Live Only</option>
            </select>
            <select className="ema-select" value={tlFilterResult} onChange={e => setTlFilterResult(e.target.value)} style={{minWidth: 80}}>
              <option value="ALL">All Results</option><option value="WIN">Wins</option><option value="LOSS">Losses</option>
            </select>
            <input type="date" className="ema-select date-input" value={tlFilterDate} onChange={e => setTlFilterDate(e.target.value)} title="From date" style={{minWidth: 120}} />
            <span style={{color: "var(--muted2)", fontSize: 11}}>→</span>
            <input type="date" className="ema-select date-input" value={tlFilterDateTo} onChange={e => setTlFilterDateTo(e.target.value)} title="To date (empty = open-ended)" style={{minWidth: 120}} />
            {ERA_VIEWS.map(era => (
              <button key={era.name} onClick={() => { setTlFilterDate(era.from); setTlFilterDateTo(""); }} title={era.title}
                style={{
                  background: tlFilterDate === era.from && !tlFilterDateTo ? "color-mix(in srgb, var(--info) 20%, transparent)" : "var(--btn-muted)",
                  color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 12,
                  fontSize: 10.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer",
                }}>{era.name}</button>
            ))}
            <select className="ema-select" value={tlFilterExpiry} onChange={e => setTlFilterExpiry(e.target.value)} title="Contract-expiry filter (0DTE = entered on its expiry day)" style={{minWidth: 100}}>
              <option value="ALL">All expiries</option><option value="EXPIRY">Expiry day</option><option value="NONEXP">Non-expiry</option>
            </select>
            {(tlFilterStatus !== "ALL" || tlFilterType !== "ALL" || tlFilterMode !== "ALL" || tlFilterResult !== "ALL" || tlFilterDate || tlFilterDateTo) && (
              <button className="btn btn-sm btn-secondary" onClick={() => { setTlFilterStatus("ALL"); setTlFilterType("ALL"); setTlFilterMode("ALL"); setTlFilterResult("ALL"); setTlFilterDate(""); setTlFilterDateTo(""); }}>Clear</button>
            )}
            <span style={{marginLeft: "auto", fontSize: 12, opacity: 0.7}}>
              {filteredTradeLog.length} trade{filteredTradeLog.length !== 1 ? "s" : ""}
              {tlSummary.open > 0 && <> | <span style={{color: "#ffa726"}}>{tlSummary.open} open</span></>}
              {tlSummary.closed > 0 && <> | {tlSummary.wins}W {tlSummary.closed - tlSummary.wins}L ({tlSummary.closed > 0 ? Math.round(tlSummary.wins / tlSummary.closed * 100) : 0}%)</>}
              {tlSummary.totalPnl !== 0 && <> | <span className={tlSummary.totalPnl >= 0 ? "positive" : "negative"}>{tlSummary.totalPnl >= 0 ? "+" : "-"}₹{Math.abs(tlSummary.totalPnl).toLocaleString("en-IN")}</span></>}
              {tlSummary.closed > 0 && tradingDaysOf(filteredTradeLog.filter(t => t.status === "CLOSED")) > 0 && <> | avg/day <b className={tlSummary.totalPnl >= 0 ? "positive" : "negative"}>{(() => { const nd = tradingDaysOf(filteredTradeLog.filter(t => t.status === "CLOSED")); const v = tlSummary.totalPnl / nd; return `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")} (${nd}d)`; })()}</b></>}
              {(() => { const pc = peakConcurrentCapital(filteredTradeLog); return pc.peak > 0 && <> | peak capital <b title={pc.at ? `most capital deployed at once${pc.at ? " (at " + pc.at.toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit",hour12:false}) + ")" : ""}` : ""}>₹{pc.peak.toLocaleString("en-IN")}</b></>; })()}
            </span>
          </div>
          <TradeCards trades={filteredTradeLog} livePremiums={livePremiums} onSelect={setDrawerTrade} onExit={exitTrade} />
          <div className="stocks-table-wrapper tl-table-wrap">
            <table className="stocks-table">
              <thead><tr>
                <th style={{width: 40}}></th><th>Time</th><th>Exit</th><th>Index</th><th>Type</th><th>Fib</th><th>Option</th><th>Qty</th><th>Entry</th><th>SL</th><th>Target</th>
                <th>Buy Prem</th><th>Sell Prem</th><th>Capital</th><th>Gross P&L</th><th>Charges</th><th>Net P&L</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {filteredTradeLog.map(t => (
                  <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : "trade-closed-row"}
                    style={{ cursor: "pointer" }} title="Click for trade details"
                    onClick={(e) => { if (e.target.closest("button,a")) return; setDrawerTrade(t); }}>
                    <td style={{whiteSpace: "nowrap"}}>
                      {t.status === "OPEN" && <button className="btn btn-sm btn-danger" onClick={() => exitTrade(t)} style={{marginRight: 2, fontSize: 10, padding: "2px 6px"}}>Exit</button>}
                      <button className="btn btn-sm" style={{background: "var(--btn-muted)", color: "var(--grey-light)", fontSize: 10, padding: "2px 6px", border: "1px solid var(--btn-border)", borderRadius: 4, cursor: "pointer"}}
                        onClick={async () => {
                          if (!window.confirm(`Delete trade ${t.tradingsymbol || t.type} @ ${t.entry}?`)) return;
                          await axios.post(`${API}/auto-trade/delete-trades`, { ids: [t.id] }).catch(() => {});
                          setTradeLog(prev => prev.filter(x => x.id !== t.id));
                        }}>Del</button>
                    </td>
                    <td>{new Date(t.time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</td><td>{t.exitTime ? new Date(t.exitTime).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td style={{fontSize: 10, color: "var(--grey-text)"}}>{t.index || "-"}</td>
                    <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                    <td><span className="fib-badge">{t.fibLevel || "-"}</span></td>
                    <td className="opt-cell">{t.signalType} {t.strike}{Array.isArray(t.aiReviews) && t.aiReviews.some(r => r.verdict === "EXIT") && <span title="AI suggested exit (advisory) — open trade details" style={{ marginLeft: 4 }}>🤖</span>}{isExpiryDay(t) && <span title="Entered on its contract\u2019s expiry day (0DTE)" style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "var(--warn-text)", border: "1px solid var(--warn-text)", borderRadius: 3, padding: "0 3px" }}>EXP</span>}</td>
                    <td>{t.qty} ({t.lots}L){t.trailExitDone && (
                      <div className="trail-detail-label" style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                        {(t.trailExitReason || "").includes("PROFIT") ? "Profit +20%" : "75% booked"}: {t.trailExitQty} @ ₹{t.trailExitPremium}
                        {t.trailExitPnl != null && <span className={t.trailExitPnl >= 0 ? "positive" : "negative"}> {t.trailExitPnl >= 0 ? "+" : ""}₹{t.trailExitPnl}</span>}
                        {t.remainingQty != null && <> · {t.remainingQty} trailing</>}
                      </div>
                    )}</td>
                    <td><a href="#chart" className="entry-chart-link" onClick={(e) => { e.preventDefault(); openTradeChart(t); }} title="View chart with signals">{t.entry?.toFixed(1)}</a></td>
                    <td className="sl">{t.sl?.toFixed(1)}</td>
                    <td>
                      {t.target?.toFixed(1)}
                      {t.targetPremium != null && (
                        <div style={{fontSize: 10, color: "#9ca3af", marginTop: 2}} title="Estimated option premium at target (0.4 delta, conservative)">
                          est. ₹{t.targetPremium.toFixed(1)}
                        </div>
                      )}
                    </td>
                    <td className="premium-cell">
                      {t.entryPremium ? `₹${t.entryPremium.toFixed(1)}`
                        : t.status === "OPEN" && livePremiums[t.tradingsymbol]
                          ? <span className="live-premium">₹{livePremiums[t.tradingsymbol].toFixed(1)}</span>
                          : "-"}
                    </td>
                    <td className="premium-cell">
                      {t.status === "OPEN" && t.entryPremium && livePremiums[t.tradingsymbol]
                        ? <span className="live-premium">₹{livePremiums[t.tradingsymbol].toFixed(1)}</span>
                        : t.exitPremium ? `₹${t.exitPremium.toFixed(1)}` : "-"}
                    </td>
                    <td className="premium-cell">{t.capital ? `₹${t.capital.toLocaleString("en-IN")}` : "-"}</td>
                    {(() => {
                      // Compute P&L from actual premiums for consistency
                      let grossPnl = null, netPnl = null, charges = t.charges || 0;
                      if (t.status === "OPEN" && livePremiums[t.tradingsymbol] && t.entryPremium) {
                        // booked tier legs are realized; live premium applies to the remaining qty only
                        const liveQty = t.remainingQty != null ? t.remainingQty : (t.qty || 130);
                        grossPnl = (t.trailExitPnl || 0) + Math.round((livePremiums[t.tradingsymbol] - t.entryPremium) * liveQty);
                        netPnl = grossPnl;
                      } else if (t.status === "CLOSED" && t.trailExitDone && t.netPnl != null) {
                        // partial releases happened — backend P&L already combines all legs
                        grossPnl = t.grossPnl; netPnl = t.netPnl;
                      } else if (t.status === "CLOSED" && t.entryPremium && t.exitPremium) {
                        grossPnl = Math.round((t.exitPremium - t.entryPremium) * (t.qty || 130));
                        // Recalculate charges from premiums
                        const bv = t.entryPremium * (t.qty || 130);
                        const sv = t.exitPremium * (t.qty || 130);
                        const tv = bv + sv;
                        const brk = Math.min(20, tv * 0.0003) * 2;
                        charges = Math.round((brk + sv * 0.000625 + tv * 0.00053 + (brk + tv * 0.00053) * 0.18 + tv * 0.000001 + bv * 0.00003) * 100) / 100;
                        netPnl = Math.round(grossPnl - charges);
                      } else {
                        grossPnl = t.grossPnl; netPnl = t.netPnl;
                      }
                      const isRunning = t.status === "OPEN" && livePremiums[t.tradingsymbol] && t.entryPremium;
                      const fmtPnl = (v) => `${v >= 0 ? "+" : "-"}₹${Math.abs(v).toLocaleString("en-IN")}`;
                      return (<>
                        <td className={grossPnl != null ? (grossPnl >= 0 ? "positive" : "negative") : ""}>
                          {grossPnl != null ? fmtPnl(grossPnl) : "-"}
                        </td>
                        <td className="premium-cell">{charges ? `₹${charges}` : "-"}</td>
                        <td className={netPnl != null ? (netPnl >= 0 ? "positive" : "negative") : ""}>
                          {isRunning
                            ? <span className="running-pnl">{fmtPnl(netPnl)} <span className="pulse-dot" style={{display:"inline-block"}}></span></span>
                            : netPnl != null ? fmtPnl(netPnl) : "-"}
                        </td>
                      </>);
                    })()}
                    <td>
                      <span className={`status-badge status-${t.status.toLowerCase()}`}>{t.status}</span><span style={{ marginLeft: 4, fontSize: 8.5, fontWeight: 800, padding: "1px 5px", borderRadius: 3, letterSpacing: 0.5, background: t.simulated === false ? "#dc2626" : "var(--btn-muted)", color: t.simulated === false ? "#fff" : "var(--muted2)", border: t.simulated === false ? "none" : "1px solid var(--btn-border)" }}>{t.simulated === false ? "LIVE" : "PAPER"}</span>
                      {t.exitReason && <span className="exit-reason-text"> ({t.exitReason})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ); })()}

      {drawerTrade && <TradeDrawer trade={drawerTrade} onClose={() => setDrawerTrade(null)} />}

      {/* Strategy Rules — collapsed by default */}
      <Collapsed title="Strategy rules — Fib confluence, bullish/bearish rejection">
      <div className="ema-rules">
        <div className="ema-rule">
          <div className="ema-rule-icon" style={{background:"#7c4dff1a",color:"#7c4dff"}}>F</div>
          <div><strong>Fibonacci Confluence</strong><p>Price reaches a key Fib level (38.2%-61.8%) that aligns with VWAP or S/R zone → high-probability reversal</p></div>
        </div>
        <div className="ema-rule">
          <div className="ema-rule-icon long-icon">L</div>
          <div><strong>Long: Bullish Rejection</strong><p>Bullish candle rejects Fib support + confluence → next candle breaks above → BUY CALL</p></div>
        </div>
        <div className="ema-rule">
          <div className="ema-rule-icon short-icon">S</div>
          <div><strong>Short: Bearish Rejection</strong><p>Bearish candle rejects Fib resistance + confluence → next candle breaks below → BUY PUT</p></div>
        </div>
      </div>
      </Collapsed>

      {/* Backtest Section — collapsed by default */}
      <Collapsed title="Backtest">
      <div className="bt-section">
        <div className="bt-header">
          <h3>Backtest</h3>
          <div className="bt-controls">
            <select className="ema-select" value={btTimeframe} onChange={e => setBtTimeframe(e.target.value)}>
              <option value="5minute">5 Min</option><option value="15minute">15 Min</option>
              <option value="30minute">30 Min</option><option value="60minute">1 Hour</option><option value="day">Daily</option>
            </select>
            <select className="ema-select" value={btDateMode} onChange={e => setBtDateMode(e.target.value)}>
              <option value="custom">Custom Dates</option>
              <option value="days">By Days</option>
            </select>
            {btDateMode === "days" ? (
              <select className="ema-select" value={btDays} onChange={e => setBtDays(e.target.value)}>
                <option value="1">1 Day</option><option value="2">2 Days</option><option value="3">3 Days</option><option value="5">5 Days</option>
                <option value="10">10 Days</option><option value="30">30 Days</option><option value="60">60 Days</option><option value="90">90 Days</option>
              </select>
            ) : (
              <>
                <input type="date" className="ema-select date-input" value={btFromDate} onChange={e => setBtFromDate(e.target.value)} title="From date" />
                <input type="date" className="ema-select date-input" value={btToDate} onChange={e => setBtToDate(e.target.value)} title="To date" />
              </>
            )}
            <select className="ema-select" value={btTarget} onChange={e => setBtTarget(e.target.value)}>
              <option value="1">Target 1:1</option><option value="1.5">Target 1:1.5</option>
              <option value="2">Target 1:2</option><option value="3">Target 1:3</option>
            </select>
            <select className="ema-select" value={btLots} onChange={e => setBtLots(e.target.value)}>
              <option value="1">1 Lot</option><option value="2">2 Lots</option><option value="3">3 Lots</option><option value="5">5 Lots</option>
              <option value="10">10 Lots</option><option value="15">15 Lots</option><option value="20">20 Lots</option><option value="25">25 Lots</option><option value="50">50 Lots</option>
            </select>
            <select className="ema-select" value={btStrike} onChange={e => setBtStrike(e.target.value)}>
              <option value="ATM">ATM</option><option value="ITM">ITM</option>
            </select>
            <label className="bt-trailing-label"><input type="checkbox" checked={btTrailing} onChange={e => setBtTrailing(e.target.checked)} /> Trailing SL</label>
            <label className="bt-trailing-label"><input type="checkbox" checked={btPartial} onChange={e => setBtPartial(e.target.checked)} /> Partial Exit</label>
            <span style={{color:"var(--edge)",margin:"0 4px"}}>|</span>
            <label className="bt-trailing-label"><input type="checkbox" checked={btVwap} onChange={e => setBtVwap(e.target.checked)} /> VWAP</label>
            <label className="bt-trailing-label"><input type="checkbox" checked={btSR} onChange={e => setBtSR(e.target.checked)} /> S/R</label>
            <label className="bt-trailing-label" style={{color:"#f59e0b"}}><input type="checkbox" checked={btVixAdaptive} onChange={e => setBtVixAdaptive(e.target.checked)} /> VIX Adaptive</label>
            <button className="btn btn-secondary" onClick={runBacktest} disabled={btLoading}>{btLoading ? "Running..." : "Run Backtest"}</button>
          </div>
        </div>
        {btData && !btLoading && (
          <div className="bt-dir-filter">
            <span className="bt-dir-label">Filter:</span>
            {["ALL", "SHORT", "LONG"].map(dir => (
              <button key={dir} className={`bt-dir-btn ${btDirFilter === dir ? "active" : ""} ${dir === "SHORT" ? "btn-short" : dir === "LONG" ? "btn-long" : ""}`}
                onClick={() => setBtDirFilter(dir)}>{dir === "ALL" ? "All" : dir}</button>
            ))}
          </div>
        )}
        {btError && <div className="error-msg">{btError}</div>}
        {btLoading && <div className="loading"><div className="spinner" /><p>Running backtest...</p></div>}
        {btData && !btLoading && <BacktestResults bt={btData} dirFilter={btDirFilter} />}
      </div>
      </Collapsed>

      {error && <div className="error-msg">{error}</div>}
      {loading && <div className="loading"><div className="spinner" /><p>Scanning Nifty for Fib + VWAP signals...</p></div>}

      {data && !loading && (
        <>
          {/* Live Watch Panel */}
          <div className="live-watch-panel">
            <div className="live-watch-header">
              <h4>Live Watch {scanning && <span className="pulse-dot" style={{display:"inline-block",marginLeft:8}}></span>}</h4>
              <span className="live-watch-time">Last scan: {lastScanTime ? lastScanTime.toLocaleTimeString("en-IN") : "-"}</span>
            </div>
            <div className="live-watch-grid">
              <div className="live-watch-card">
                <div className="live-watch-label">Nifty</div>
                <div className="live-watch-value">{data.lastCandle?.close?.toFixed(1)}</div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">VWAP</div>
                <div className="live-watch-value" style={{color:"#42a5f5"}}>{data.vwapCurrent}</div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">Trend</div>
                <div className="live-watch-value" style={{color: data.fibData?.isUptrend ? "#69f0ae" : "#ff5252", fontSize: 13}}>
                  {data.fibData?.isUptrend ? "UPTREND" : "DOWNTREND"}
                </div>
              </div>
              <div className="live-watch-card">
                <div className="live-watch-label">Swing Range</div>
                <div className="live-watch-value" style={{fontSize: 12}}>
                  H: {data.fibData?.swingHigh?.price?.toFixed(0)} — L: {data.fibData?.swingLow?.price?.toFixed(0)}
                </div>
              </div>
            </div>
            {/* Fib levels being watched */}
            {data.fibData?.levels && (
              <div className="live-watch-fib-levels">
                <div className="live-watch-sublabel">Watching Fib Levels:</div>
                <div className="live-watch-fib-row">
                  {data.fibData.levels.filter(l => l.level >= 0.2 && l.level <= 0.8).map((l, i) => {
                    const dist = Math.abs(data.lastCandle?.close - l.price);
                    const isNear = dist < (data.fibData.swingHigh.price - data.fibData.swingLow.price) * 0.03;
                    return (
                      <span key={i} className={`fib-watch-chip ${isNear ? "fib-watch-near" : ""}`}>
                        {l.name}: {l.price}
                        {isNear && <span className="fib-near-dot"></span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {/* S/R zones being watched */}
            {data.srZones?.length > 0 && (
              <div className="live-watch-fib-levels">
                <div className="live-watch-sublabel">S/R Zones:</div>
                <div className="live-watch-fib-row">
                  {data.srZones.slice(0, 5).map((z, i) => {
                    const isNear = Math.abs(data.lastCandle?.close - z.center) < (data.lastCandle?.close * 0.003);
                    return (
                      <span key={i} className={`sr-watch-chip ${isNear ? "fib-watch-near" : ""} ${z.center < data.lastCandle?.close ? "sr-chip-support" : "sr-chip-resistance"}`}>
                        {z.center} ({z.touches}t)
                        {isNear && <span className="fib-near-dot"></span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Probable entry */}
            {data.currentSetup ? (
              <div className={`live-watch-setup ${data.currentSetup.type === "BEARISH_WATCH" ? "setup-bearish" : "setup-bullish"}`}>
                <div className="live-watch-setup-header">
                  <span className={data.currentSetup.type === "BEARISH_WATCH" ? "negative" : "positive"} style={{fontWeight:700}}>
                    {data.currentSetup.type === "BEARISH_WATCH" ? "PROBABLE SHORT" : "PROBABLE LONG"} at Fib {data.currentSetup.fibLevel} ({data.currentSetup.fibPrice})
                  </span>
                  <span style={{color:"#78909c",fontSize:11}}>
                    {data.currentSetup.vwapConfluence ? "VWAP " : ""}{data.currentSetup.srConfluence ? "S/R " : ""}confluence
                  </span>
                </div>
                <div className="live-watch-levels">
                  {data.currentSetup.entryAbove && <div className="live-watch-level"><span className="level-dot" style={{background:"#42a5f5"}}></span>Entry above: <strong>{data.currentSetup.entryAbove.toFixed(1)}</strong></div>}
                  {data.currentSetup.entryBelow && <div className="live-watch-level"><span className="level-dot" style={{background:"#42a5f5"}}></span>Entry below: <strong>{data.currentSetup.entryBelow.toFixed(1)}</strong></div>}
                  <div className="live-watch-level"><span className="level-dot" style={{background:"#ff5252"}}></span>SL: <strong>{data.currentSetup.stopLoss?.toFixed(1)}</strong></div>
                </div>
              </div>
            ) : (
              <div className="live-watch-no-setup">No confluence setup — monitoring Fib levels + VWAP + S/R zones</div>
            )}
          </div>

          {/* Current Setup Alert */}
          {data.currentSetup && (
            <div className={`ema-alert ${data.currentSetup.type === "BEARISH_WATCH" ? "alert-bearish" : "alert-bullish"}`}>
              <div className="ema-alert-header">
                <span className="ema-alert-type">{data.currentSetup.type === "BEARISH_WATCH" ? "BEARISH SETUP" : "BULLISH SETUP"}</span>
                <span className="ema-alert-live">LIVE</span>
              </div>
              <p>{data.currentSetup.message}</p>
              <div className="ema-alert-levels">
                <span>Fib: <strong>{data.currentSetup.fibLevel} ({data.currentSetup.fibPrice})</strong></span>
                <span>VWAP: <strong>{data.currentSetup.vwapPrice}</strong></span>
                <span>SL: <strong>{data.currentSetup.stopLoss?.toFixed(1)}</strong></span>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="scan-summary">
            <div className="summary-card"><div className="summary-value">{data.lastCandle?.close?.toFixed(1)}</div><div className="summary-label">Spot Price</div></div>
            <div className="summary-card"><div className="summary-value">{data.vwapCurrent}</div><div className="summary-label">VWAP</div></div>
            <div className="summary-card"><div className="summary-value">{data.totalSignals}</div><div className="summary-label">Total Signals</div></div>
            <div className="summary-card"><div className="summary-value">{data.srZones?.length || 0}</div><div className="summary-label">S/R Zones</div></div>
          </div>

          {/* Fib Levels + S/R Zones side by side */}
          <div className="fib-sr-grid">
            <FibLevelsCard fibData={data.fibData} spotPrice={data.lastCandle?.close} />
            <SRZonesCard zones={data.srZones} spotPrice={data.lastCandle?.close} />
          </div>

          {/* Recent Signals */}
          {data.signals?.length > 0 && (
            <div className="ema-signals-list">
              <h3>Recent Signals</h3>
              {data.signals.slice().reverse().map((sig, idx) => (
                <div key={idx} className={`ema-signal-card ${sig.type === "SHORT" ? "signal-short" : "signal-long"}`}>
                  <div className="ema-signal-header">
                    <span className={`signal-type-badge ${sig.type === "SHORT" ? "badge-short" : "badge-long"}`}>{sig.type}</span>
                    <span className="fib-badge">{sig.fibLevel}</span>
                    <span className="signal-date">{formatDate(sig.triggerCandle?.date)}</span>
                  </div>
                  <p className="signal-detail-text">{sig.detail}</p>
                  <div className="ema-signal-levels">
                    <div className="level"><span className="level-label">Entry</span><span className="level-value">{sig.entryPrice?.toFixed(1)}</span></div>
                    <div className="level"><span className="level-label">SL</span><span className="level-value sl">{sig.stopLoss}</span></div>
                    <div className="level"><span className="level-label">Risk</span><span className="level-value">{sig.risk} pts</span></div>
                    <div className="level"><span className="level-label">Confluence</span><span className="level-value">{sig.confluence?.join(" + ") || "-"}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!data && !loading && !error && !btData && (
        <div className="scan-empty">
          <h3>Fibonacci + VWAP + S/R Scanner</h3>
          <p>The server scanner runs automatically during market hours (09:15–15:30 IST) — trades appear in the Trade Logs below as signals fire. To run a manual chart scan, expand “Scan & strategy controls” above.</p>
        </div>
      )}
    </div>
  );
}
